import CONSTANTS from "@/constants.js";
import axios from "axios";
import Vue from "vue";
import { cloneDeep, isNumber } from "lodash";
import router from "@/router";
const AUTH_TOKEN = process.env.VUE_APP_THREEKIT_TOKEN;
const TK_ENV = `${process.env.VUE_APP_THREEKIT_ENDPOINT}/`;
const ORG_ID = process.env.VUE_APP_THREEKIT_ORG_ID;
const TK_FULLASSET_API = "api/products/export/json";
const TK_ASSET_API = "api/assets";
const DATATABLE_ID = "0ddf38ac-968d-42d2-b7ff-83e123e9d64e";
const DATATABLE_PLP_TO_3D_LAYOUTS = "ecf5183d-057f-4cd6-a6be-0860f3ab3a3f";
const STAGE_ID_LAYERS = "5812a1f3-45bf-468e-9846-5979487ae758";
const STAGE_ID_BUILDER = "0cd6ddae-d9ee-4765-bcdc-781a8101bfb8";

const threekit = {
  namespaced: true,

  state: () => ({
    globalConfigurator: null,
    designConfigurator: null,
    threekitAvailableColors: [],
    fonts: [],

    //Cache
    cachedTKData: {
      products: {},
      datatables: {},
      fonts: {},
    },

    //Colors
    sublimationColors: [],
    stockColors: [],
    materialColors: [],

    //Decorations
    decorationsConfigurator: null,
    activeDecorationAttributes: null,
    decorationsPrice: 0,

    spiritwearThumbnail: null,
    uniformThumbnail: null,

    //For the catalog's previews
    PLPDecorationsList: [],

    //For the 3D Player
    threekit3DLayouts: null,

    //For the 3D Player
    decorations3DList: [],

    //For the 3D Player
    activeDecoration: null,

    //For the 3D Player
    reversibleView: "Outside",

    //UI
    loading: false,
    completeLoader: true,
    playerLoader: false,
    playerLoaderMessage: "",
    loadingMessage: "",

    //Loading states
    threeekitFontsIsLoading: false,
    threekitLoadingDataset: {},
  }),

  getters: {
    getNestedConfigurator:
      (_, getters) =>
      ({ parentConfigurator, attributeName }) => {
        //To Modify an Attribute that is a nested property (Ex: Color)
        // first we need to get that attribute from configurator.getDisplayAttributes(),
        const attribute = getters["getAttributeByName"]({
          configurator: parentConfigurator,
          attributeName,
        });
        //Then, we need to create a 'Nested Configurator', passing the whole attribute as a parameter
        //it will allow us to modify the nested property through the setConfiguration method
        const nestedConfigurator =
          parentConfigurator?.getNestedConfigurator(attribute);

        return nestedConfigurator;
      },

    getAttributeByName:
      () =>
      ({ configurator, attributeName }) => {
        return configurator
          ?.getDisplayAttributes()
          ?.find((attr) => attr.name === attributeName);
      },

    //Product
    isSublimated: (state) => (styleName) => {
      return (
        state.cachedTKData.products?.[styleName]?.item?.attributes?.find(
          (attr) => attr?.proxy && attr?.proxy?.name === "Style Category"
        )?.defaultValue === "Sublimation"
      );
    },

    isSublimated3D: (_state, getters, rootState) => {
      return getters["isSublimated"](
        getters.activeStyleName || rootState.project.activeProduct?.style_id
      );
    },

    activeStyleName: (_state, _getters, rootState) => {
      return rootState.project?.activeProduct?.style_id;
    },

    activeProduct(state, getters) {
      return state.cachedTKData.products[getters.activeStyleName];
    },

    getProductByStyleName: (state) => (styleName) => {
      return state.cachedTKData.products?.[styleName];
    },

    //Colors

    mapThreekitColorWithConstantColors:
      (_, getters) =>
      (threekitColors, typeOfColors, checkMetadata = true) => {
        //typeOfColors -> 'sublimation' / 'stock'
        const isSublimation = typeOfColors === "sublimation";

        const colors = cloneDeep(CONSTANTS.COLORS)
          ?.map((gameplanColor) =>
            cloneDeep(threekitColors)
              ?.map((tkColor) => {
                const gameplanColorName = isSublimation
                  ? gameplanColor.chromagear_name
                  : gameplanColor.stock_name;
                if (
                  getters.doColorsNamesMatch(tkColor, gameplanColorName) &&
                  (checkMetadata
                    ? tkColor?.metadata?.["Gameplan"] === "Yes"
                    : true)
                ) {
                  //Append the threekit color data
                  gameplanColor.assetId = tkColor?.id;
                  gameplanColor.name = tkColor?.name;
                  gameplanColor.metadata = tkColor?.metadata;
                  gameplanColor.tags = tkColor?.tags;
                  gameplanColor.isAvailable = true;
                  return gameplanColor;
                }
                return null;
              })
              .filter((c) => c && c?.isAvailable)
          )
          .map((c) => (c && c?.length > 0 ? c[0] : null))
          .filter((c) => c);

        return colors;
      },

    getThreekitColorAssetIdByGameplanId:
      (_state, getters, _rootState, rootGetters) =>
      (
        gameplanId,
        backupColorName,
        colorPalette = "sublimation",
        log = false
      ) => {
        if (log) console.log({ gameplanId, backupColorName, colorPalette });
        const isSublimation = colorPalette === "sublimation";
        if (gameplanId?.id) gameplanId = gameplanId.id;
        const gameplanColor = rootGetters["project/getColorById"](gameplanId);

        const threekitColor = getters["findThreekitColorByName"](
          isSublimation
            ? gameplanColor?.chromagear_name
            : gameplanColor?.stock_name,
          `${colorPalette}Colors`,
          backupColorName
        );
        if (log) {
          console.log({ gameplanColor, threekitColor });
        }
        return threekitColor?.assetId;
      },

    getThreekitColorAssetIdByGameplanIdShowroom:
      (_state, getters, _rootState, rootGetters) =>
      (gameplanId, backupColorName, colorPalette) => {
        if (gameplanId?.id) gameplanId = gameplanId.id;
        const gameplanColor = rootGetters["project/getColorById"](gameplanId);
        const threekitColor = getters["findThreekitColorByName"](
          gameplanColor?.name,
          `${colorPalette}Colors`,
          backupColorName
        );
        return threekitColor?.assetId;
      },

    findThreekitColorByName:
      (state, getters) =>
      (gameplanColorName, colorPalette, backupColorName) => {
        const finalColor = [...state[colorPalette]].find((tkColor) =>
          getters.doColorsNamesMatch(tkColor, gameplanColorName)
        );
        const backupColor = [...state[colorPalette]].find((tkColor) =>
          getters.doColorsNamesMatch(tkColor, backupColorName || "BLACK")
        );

        return finalColor || backupColor;
      },

    findThreekitColorByAssetId:
      (state, getters) =>
      (gameplanColorAssetId, useBackup = true) => {
        const sublimationColor = state["sublimationColors"].find(
          (tkColor) => tkColor?.assetId === gameplanColorAssetId
        );
        const sublimationBackupColor = state["sublimationColors"].find(
          (tkColor) => getters.doColorsNamesMatch(tkColor, "BLACK")
        );
        const stockColor = state["stockColors"].find(
          (tkColor) => tkColor?.assetId === gameplanColorAssetId
        );
        const stockBackupColor = state["stockColors"].find((tkColor) =>
          getters.doColorsNamesMatch(tkColor, "BLACK")
        );
        return (
          sublimationColor ||
          stockColor ||
          (useBackup && sublimationBackupColor) ||
          (useBackup && stockBackupColor)
        );
      },

    findStockColorOnStockProductByName:
      (state, getters) =>
      ({ styleName, gameplanColorName, returnBackup = true }) => {
        const stockColorOptions = state.cachedTKData.products[
          styleName
        ]?.stock?.filter((c) => c && !c?.tags?.includes("color_inside"));
        const finalColor = stockColorOptions?.find((tkColor) =>
          getters.doColorsNamesMatch(tkColor, gameplanColorName)
        );
        const backupColor = stockColorOptions?.find((tkColor) =>
          getters.doColorsNamesMatch(tkColor, "BLACK")
        );

        return finalColor ? finalColor : returnBackup ? backupColor : null;
      },
    findStockColorOnStockProductByAssetId:
      (state, getters) =>
      ({ styleName, assetId, returnBackup = true }) => {
        const stockColorOptions = state.cachedTKData.products[
          styleName
        ]?.stock?.filter((c) => c && !c?.tags?.includes("color_inside"));
        const finalColor = stockColorOptions?.find(
          (tkColor) => tkColor.id === assetId
        );
        const backupColor = stockColorOptions?.find((tkColor) =>
          getters.doColorsNamesMatch(tkColor, "BLACK")
        );

        return finalColor ? finalColor : returnBackup ? backupColor : null;
      },

    getColorsAndDesignConfiguration:
      (_, getters) =>
      ({ styleName, designId, colors }) => {
        const isSublimated = getters["isSublimated"](styleName);

        //Sublimated -> 3 colors
        //Stock -> only the first color

        //We will only send the colors that are filled as parameters, otherwise we would get a bugged layer
        //todo, take the colors from the product, and the project colors as backups
        const filledDesignColors = colors.filter((c) => {
          return c && c.color && c.color?.name;
        });

        const getColorsConfiguration = (colors) =>
          colors?.reduce((acc, color) => {
            if (color?.color?.name) {
              const colorKey = getters.getThreekitColorKey(color?.key);

              const colorName = isSublimated
                ? color?.color?.chromagear_name
                : color?.color?.stock_name;

              const colorAssetId = getters.findThreekitColorByName(
                colorName,
                isSublimated ? "sublimationColors" : "stockColors",
                isSublimated
                  ? getters.projectColors?.[1]?.color?.chromagear_name
                  : getters.projectColors?.[1]?.color?.stock_name
              )?.assetId;

              const colorParam = {
                [colorKey]: {
                  assetId: colorAssetId,
                },
              };
              return { ...acc, ...colorParam };
            } else {
              return acc;
            }
          }, {});

        //Sublimated colors config
        if (isSublimated) {
          const colorsConfiguration =
            getColorsConfiguration(filledDesignColors);

          const configuration = {
            Design: {
              type: "item",
              assetId: designId,
              configuration: {
                ...colorsConfiguration,
              },
            },
          };

          //Reversible config
          if (getters["isReversible"](styleName)) {
            const reversibleColors = filledDesignColors.map((color, index) => {
              //Swap C1 and C2 keys
              if (index === 0 && color) {
                color.key = "color_2";
              }
              if (index === 1 && color) {
                color.key = "color_1";
              }

              return color;
            });

            const reversibleColorsConfiguration =
              getColorsConfiguration(reversibleColors);

            //Change the color orders (C2, C1, C3)
            configuration["Design (Reverse)"] = {
              ...configuration["Design"],
              configuration: reversibleColorsConfiguration,
            };
          }

          return configuration;
        } else {
          //Stock color config
          const colorAssetId = getters.findStockColorOnStockProductByName({
            styleName,
            gameplanColorName: filledDesignColors[0]?.color?.stock_name,
          })?.id;
          const configuration = {
            Color: {
              type: "item",
              assetId: colorAssetId,
              configuration: "",
            },
          };

          return configuration;
        }
      },

    getFontByAssetId: (state) => (assetId) => {
      return state.fonts.find((font) => font.id === assetId);
    },

    //Designs

    designs: (state, _getters, rootState) => {
      //Available Designs
      const styleName = rootState.project.activeProduct?.styleName;
      const designs = state.cachedTKData?.products?.[styleName]?.designs;

      return designs
        ? Object.keys(designs)?.map((designName) => {
            const design = designs[designName];
            return {
              styleName,
              assetId: design?.item?.id,
              name: design?.item?.name,
              metadata: design?.item?.metadata?.map((data) => ({
                [data.name]: data.defaultValue,
              })),
              tags: design?.item?.tags,
              tagsids: design?.item?.tagsids,
            };
          })
        : null;
    },

    activeDesignAttribute: (state, getters) => {
      return getters.getAttributeByName({
        configurator: state.globalConfigurator,
        attributeName: "Design",
      });
    },

    doColorsNamesMatch: () => (threekitColor, gameplanColorName) => {
      //Check if the colors match, replacing spaces with dashes
      //Normalize the color names, replacing spaces by hyphens
      const gameplanColorNameNormalized = gameplanColorName
        ?.trim()
        ?.replace(/\s+|_+|-+|\/+/g, " ")
        .toLowerCase();
      const gameplanColorSplit = gameplanColorNameNormalized?.split(" ");

      //Split those color names by the hyphens
      const threeKitNameNormalized = threekitColor?.name
        ?.trim()
        ?.replace(/\s+|_+|-+|\/+/g, " ")
        .toLowerCase();
      const threeKitNameNormalizedSplit = threeKitNameNormalized?.split(" ");

      //Use the first position of the splitted color names to check if they coincide
      //Ex: Columbia-blue and Columbia Blue will match

      //Words exceptions. For some colors, th first word could be the same for different colors
      //Ex: Electric blue and Electir pink
      const exceptions = ["oxford", "hex", "heather"];

      exceptions.map((exception) => {
        //Remove the words
        const gmExceptionIndex = gameplanColorSplit?.indexOf(exception);
        if (gmExceptionIndex > -1) {
          gameplanColorSplit.splice(gmExceptionIndex, 1);
        }
        //Remove the words
        const tkExceptionIndex =
          threeKitNameNormalizedSplit?.indexOf(exception);
        if (tkExceptionIndex > -1) {
          threeKitNameNormalizedSplit.splice(tkExceptionIndex, 1);
        }
      });
      // console.log({ gameplanColorSplit, threeKitNameNormalizedSplit });
      // return gameplanColorSplit && threeKitNameNormalizedSplit && gameplanColorSplit[0] === threeKitNameNormalizedSplit[0]
      return (
        gameplanColorSplit &&
        threeKitNameNormalizedSplit &&
        threeKitNameNormalizedSplit?.some((c) => c === gameplanColorSplit?.[0])
      );
    },

    getThreeKitColor: (state, getters) => (colorName) => {
      if (!state.designConfigurator) return null;

      const threekitColor = getters["getAttributeByName"]({
        configurator: state.designConfigurator,
        attributeName: colorName,
      });

      const threekitColorValue = threekitColor?.values?.find((c) => {
        //Has an AssetId inside the value prop
        if (threekitColor?.value?.assetId) {
          return c?.assetId === threekitColor?.value?.assetId;
        } else {
          //The value is a string, with the color name
          return c?.name === threekitColor?.value;
        }
      });

      return { threekitColor, threekitColorValue };
    },

    getDesignColor:
      (state, getters, rootState, rootGetters) =>
      ({ colorName, index }) => {
        let threekitColor = {};
        let finalColorValue = {};
        //Match colors by project colors or colors that came from a saved locker product
        if (!state.designConfigurator) {
          rootState.project?.activeProduct?.["product_colors"]
            ? //Saved colors from locker product
              (threekitColor = state.sublimationColors?.find(
                (sublimationColor) => {
                  return (
                    sublimationColor?.metadata?.["Color Shortcode"] ===
                    rootState.project?.activeProduct?.["product_colors"][index]
                  );
                }
              ))
            : //Project Colors
              (threekitColor = getters.findThreekitColorByName(
                rootGetters["project/projectColors"]?.[index]?.color
                  ?.chromagear_name,
                "sublimationColors"
              ));

          finalColorValue = getters.findThreekitColorByAssetId(
            threekitColor?.assetId
          );
        } else {
          threekitColor = getters.getThreeKitColor(colorName)["threekitColor"];
          finalColorValue = getters.findThreekitColorByAssetId(
            threekitColor?.value?.assetId
          );
        }

        return {
          color: finalColorValue,
          key: colorName.trim().toLowerCase().replace(/\s+/g, "_"), //we need this to open the color picker
        };
      },

    designColors(_, getters) {
      return [
        getters["getDesignColor"]({ colorName: "Color 1", index: 0 }),
        getters["getDesignColor"]({ colorName: "Color 2", index: 1 }),
        getters["getDesignColor"]({ colorName: "Color 3", index: 2 }),
      ];
    },

    getThreekitColorKey: () => (colorKey) => {
      //We will use this to change the color key that comes from the color picker
      const threekitColorKey =
        colorKey.charAt(0)?.toUpperCase() +
        colorKey.replace(/_/g, " ").slice(1);

      return threekitColorKey;
    },

    //Stock
    actualVariant: (state, getters) => {
      return getters.getAttributeByName({
        configurator: state.globalConfigurator,
        attributeName: "Color",
      });
    },

    //Principal Color
    activeProductPrincipalColors: (state, getters) => {
      return getters["isSublimated3D"]
        ? getters["designColors"]?.map((designColor) => {
            return state.sublimationColors?.find(
              (c) => c?.assetId === designColor?.color?.assetId
            )?.["metadata"]?.["Color Shortcode"];
          })
        : [
            getters["activeProduct"]?.stock?.find(
              (variant) =>
                variant.id === getters["actualVariant"]?.value?.assetId
            )?.metadata?.["shortCode"],
          ];
    },

    //2D Assets

    getUrl: () => (assetId, configuration, stageConfiguration) => {
      return `${TK_ENV}api/layers/layer?assetId=${assetId}&assetLayer=${assetId}&stageId=${STAGE_ID_LAYERS}&stageConfiguration=${encodeURIComponent(
        JSON.stringify(stageConfiguration)
      )}&assetLayerConfiguration=${encodeURIComponent(
        JSON.stringify(configuration)
      )}&orgId=${ORG_ID}&bearer_token=${AUTH_TOKEN}&format=png&resultType=file`;
    },

    get2DGroupedLayers:
      (state, getters, _, rootGetters) =>
      ({
        styleName,
        designName,
        color1 = "WHITE",
        color2 = "WHITE",
        color3 = "WHITE",
        asset1 = "",
        asset2 = "",
        asset3 = "",
        view = "Front",
        reverse = false,
      }) => {
        styleName = styleName?.toUpperCase();
        designName = designName ? designName?.toUpperCase() : "";
        // console.log('get2DGroupedLayers colors', { color1, color2, color3 });
        const stageConfiguration = { View: view };

        //Return an empty array if something is missing
        if (!state.cachedTKData.products[styleName]) {
          return [];
        }

        //Branching condition of sublimation item or stock
        const styleItem = state.cachedTKData.products[styleName]?.item;
        const isSublimated = getters["isSublimated"](styleName);

        if (isSublimated) {
          const designItem =
            state.cachedTKData.products[styleName]?.designs[designName]?.item;

          // console.log(
          //   "cached designs: ",
          //   state.cachedTKData.products[styleName]?.designs,
          //   " for designName " + designName
          // );

          // console.log("deisgitem0", designItem);

          const layerAttributes =
            state.cachedTKData.products[styleName]?.designs[designName]
              ?.layerAttributes;
          const layerItems =
            state.cachedTKData.products[styleName].designs[designName]?.layers;

          // console.log("layerAttributes", layerAttributes);
          // console.log("layerItems", layerItems);

          return layerAttributes?.map((attribute) => {
            const layerItem = layerItems?.find(
              (item) => item.id === attribute.defaultValue.assetId
            );

            // console.log("layerItem", layerItem);

            const colorGroup = layerItem?.metadata["Color Group"];
            const color =
              colorGroup === 1
                ? color1
                : colorGroup === 2
                ? color2
                : colorGroup === 3
                ? color3
                : null;

            const colorForcedByAsset =
              colorGroup === 1
                ? asset1
                : colorGroup === 2
                ? asset2
                : colorGroup === 3
                ? asset3
                : null;

            const threekitColor = getters.findThreekitColorByName(
              color,
              "sublimationColors"
            );
            const colorAssetId = threekitColor?.assetId || color;

            const configuration = {
              Design: {
                assetId: designItem?.id,
                configuration: {
                  [attribute.name]: {
                    type: "item",
                    assetId: attribute.defaultValue.assetId,
                    configuration: color
                      ? {
                          Fill: {
                            type: "item",
                            assetId: colorForcedByAsset || colorAssetId,
                            configuration: "",
                          },
                        }
                      : "",
                  },
                },
              },

              Decorations: { assetId: "" },
              "Render Layer": layerItem?.metadata.Order,
            };

            if (reverse) {
              configuration["Reversible View"] = "Inside";
            }

            return getters.getUrl(
              styleItem?.id,
              configuration,
              stageConfiguration
            );
          });
        } else {
          const stockColorOptions = state.cachedTKData.products[
            styleName
          ]?.stock?.filter((c) => c && !c?.tags?.includes("color_inside"));
          const color =
            stockColorOptions?.find((threekitColor) =>
              getters["doColorsNamesMatch"](threekitColor, color1)
            ) ||
            stockColorOptions?.find((threekitColor) =>
              getters["doColorsNamesMatch"](
                threekitColor,
                rootGetters["project/projectColors"]?.[1]?.color?.stock_name
              )
            ) ||
            stockColorOptions?.find((threekitColor) =>
              getters["doColorsNamesMatch"](
                threekitColor,
                rootGetters["project/projectColors"]?.[2]?.color?.stock_name
              )
            ) ||
            stockColorOptions?.find((threekitColor) =>
              getters["doColorsNamesMatch"](threekitColor, "black")
            ) ||
            stockColorOptions?.find((threekitColor) =>
              getters["doColorsNamesMatch"](threekitColor, "white")
            ) ||
            stockColorOptions?.[0];

          // console.log('grouped color', color);
          const configuration = {
            Color: {
              type: "item",
              assetId: asset1 || color?.id,
              configuration: "",
            },
            Decorations: { assetId: "" },
          };
          if (reverse) {
            configuration["Reversible View"] = "Inside";
          }
          return [
            getters.getUrl(styleItem?.id, configuration, stageConfiguration),
          ];
        }
      },

    //DECORATIONS
    getDecorationAttributes: (state, getters) => (locationName) => {
      const activeDecorationConfigurator = getters.getNestedConfigurator({
        parentConfigurator: state.decorationsConfigurator,
        attributeName: locationName,
      });

      return activeDecorationConfigurator?.getDisplayAttributes();
    },

    decorationsConfiguratorAttributes(state) {
      return state.decorationsConfigurator?.getDisplayAttributes();
    },

    activeDecorationAvailableLocationsByContentType(_state, getters) {
      //We are getting an object (as an string) that indicates wich locations are available depending on the content type
      const str = getters.decorationsConfiguratorAttributes?.filter(
        (attr) => attr?.name === "Available Locations For Content Type"
      )[0].value;

      return str ? JSON.parse(str) : {};
    },

    activeDecorationAvailableLocations: (state, getters) => (contentTypes) => {
      const allLocations = contentTypes.map((contentType) => {
        const locations =
          getters.activeDecorationAvailableLocationsByContentType[
            contentType || state.activeDecoration?.configuration["Content Type"]
          ]?.filter((location) => {
            //Return the location that are not beign used by the current decorations

            const locationsInUse =
              getters.decorations3DListByReversibleView?.map(
                (decoration) => decoration.locationName
              );
            const isLocationInUse = locationsInUse.includes(location);
            /*Return :
            1.the locations that are not being used by the current decorations
            2.The original location, because we might need it
          */
            return (
              !isLocationInUse ||
              location === state.activeDecoration?.originalDecorationLocation
            );
          });

        const finalLocation = locations?.length > 0 ? locations[0] : null;
        return finalLocation ? { location: finalLocation, contentType } : null;
      });

      return allLocations.filter((location) => location);
    },

    getDecorationDefaultConfiguration: (_state, getters) => (locationName) => {
      //This are the attributes we want to set to default values
      const attributesToSetAsDefault = [
        "Logo",
        "Text",
        "Font",
        "Content Type",
        "Text Fill",
        "Text Outline 1",
        "Text Outline 2",
      ];

      //Filter the attributes

      const defaultDecorations = getters
        .getDecorationAttributes(locationName)
        ?.filter((decoAttr) =>
          attributesToSetAsDefault.find((attr) => attr === decoAttr.name)
        )
        ?.reduce((acc, attr) => {
          switch (attr.name) {
            // Set the different attributes to default values. For some reason we can't use the default values that the configurator provides 😢
            case "Logo":
              acc[attr.name] = { assetId: "" };
              break;
            case "Text":
              acc[attr.name] = "";
              break;
            // case 'Font':
            //   acc[attr.name] = { "assetId": "" }
            // break;
            case "Content Type":
              acc[attr.name] = attr.defaultValue || "";
              break;
            case "Text Fill":
              acc[attr.name] = { assetId: "" };
              break;
            case "Text Outline 1":
              acc[attr.name] = { assetId: "" };
              break;
            case "Text Outline 2":
              acc[attr.name] = { assetId: "" };
              break;
            default:
              break;
          }
          return acc;
        }, {});

      return defaultDecorations;
    },

    decorations3DListByReversibleView: (state) => {
      //Filter the decoration list by the reversible view (outside/inside)
      return state.decorations3DList?.filter(
        (decoration) => decoration?.reversibleView === state.reversibleView
      );
    },

    locationNameIsSpiritwear: (state) => (locationName) => {
      return (
        state.cachedTKData.datatables[DATATABLE_ID][locationName] &&
        state.cachedTKData.datatables[DATATABLE_ID][locationName].configuration
          .Type === "Spiritwear"
      );
    },

    spiritwearLocationInSports:
      //todo if showroom, snap...

      (_state, _getters, rootState) => (locationName) => {
        const data = rootState.showroom.catalogs.map((catalog) => {
          if (!catalog.selected) return null;
          const hasLocationName = catalog.products.find((product) => {
            return product.threekit_layout === locationName;
          });

          return hasLocationName ? catalog.name : null;
        });

        return data.filter((d) => {
          return d != null;
        });
      },

    getCenterDecorationBySport:
      //todo if showroom, snap...
      (_state, _getters, rootState) => (sportName) => {
        const data = rootState.showroom.catalogs.find((catalog) => {
          return (
            catalog.selected === true &&
            catalog.name.toLowerCase() === sportName.toLowerCase()
          );
        });

        return data ? data.asset_id : ""; //default goes here too?
      },

    fullBlockFont: (state) => {
      return state.fonts.find((font) => {
        return font.name.toLowerCase() === "full block";
      });
    },

    //Reversible getters

    isReversible: (state) => (styleName) => {
      if (!state.cachedTKData.products[styleName]) return null;
      return state.cachedTKData.products[styleName].item?.metadata?.some(
        (m) => m.name === "Reversible"
      );
    },

    isReversible3D: (_, getters) => {
      return getters.isReversible(getters["activeStyleName"]);
    },

    isActiveProductReversible: (state) =>
      state.globalConfigurator
        ?.getDisplayAttributes()
        ?.some((attr) => attr.name?.includes("Reversible")),
    decorationKey: (state) =>
      state.reversibleView === "Outside"
        ? "Decorations"
        : "Decorations (Reverse)",
    designKey: (state) =>
      state.reversibleView === "Outside" ? "Design" : "Design (Reverse)",
    stockDesignKey: (state) =>
      state.reversibleView === "Outside" ? "Color" : "Color (Reverse)",
  },

  actions: {
    async initPlayerConfigurator(
      { rootState, state, getters, rootGetters, dispatch, commit },
      { styleName, designName }
    ) {
      designName = designName && designName.toUpperCase();

      const isSublimated = getters["isSublimated"](styleName);

      try {
        if (window.threekitPlayer) {
          //Loader Messages

          await dispatch("setLoadingState", {
            loadingState: true,
            message: "Initializing 3D Experience...",
          });

          //Get product data

          const productData = await dispatch(
            "project/getProductByStyleId",
            styleName,
            { root: true }
          );

          await dispatch(
            "project/setActiveProduct",
            { product: { ...rootState.project.activeProduct, ...productData } },
            { root: true }
          );

          //restart player
          await dispatch("resetThreekitPlayer");

          //Get the product info
          await dispatch("initProductCache", { styleName, designName });
          await dispatch("getThreekit3DLayoutsList");

          //Hide complete loader
          await dispatch("setCompleteLoaderState", { loadingState: false });

          //Get Decorations Bundle assetId
          const decorationsBundleId = await dispatch(
            "project/getProductDecorationsBundleAssetId",
            { styleName },
            { root: true }
          );

          const styleAssetId = state.cachedTKData.products[styleName].item.id;
          const designId =
            state.cachedTKData.products[styleName].designs[designName]?.item
              ?.id;
          const colorsAndDesignConfiguration =
            getters.getColorsAndDesignConfiguration({
              styleName,
              designId,
              colors: rootGetters["project/projectColors"],
            });

          if (isSublimated) {
            //Init cache for all designs

            const promises = productData?.["threekit_designs"]?.map(
              async (design) => {
                return await dispatch("initProductCache", {
                  styleName,
                  designName: design.design_name,
                });
              }
            );
            Promise.all(promises);
          }

          const previousConfigId =
            router.currentRoute.query.short_id ||
            rootState.project.activeProduct.shortId;

          const previousConfiguration =
            previousConfigId &&
            (await dispatch("getSavedProductConfiguration", previousConfigId));

          const initialDecorationsConfiguration = previousConfiguration
            ? null
            : await dispatch("initDecorationsConfiguration", {
                decorationsBundleId,
              });

          const initialConfiguration = previousConfiguration
            ? { ...previousConfiguration.variant }
            : {
                ...colorsAndDesignConfiguration,
                ...initialDecorationsConfiguration,
              };

          await dispatch("setLoadingState", { loadingState: false });

          //Get PLP to 3D Layouts

          Vue.nextTick(async () => {
            await dispatch("setPlayerLoadingState", {
              loadingState: true,
              message: "Initializing 3D Experience ...",
            });

            setTimeout(async () => {
              await dispatch("setPlayerLoadingState", {
                loadingState: true,
                message: "Loading Style Information ...",
              });
            }, 1000);
            setTimeout(async () => {
              await dispatch("setPlayerLoadingState", {
                loadingState: true,
                message: "Preparing for Customization ...",
              });
            }, 2000);

            try {
              const api =
                window.playerApi ||
                (await window.threekitPlayer({
                  authToken: AUTH_TOKEN,
                  el: document.getElementById("threekit-player"),
                  assetId: STAGE_ID_BUILDER,
                  cache: {
                    maxAge: 604800,
                  },
                  showConfigurator: false,
                  publishStage: "draft",
                  initialConfiguration,
                  showLoadingProgress: {
                    enabled: false,
                  },
                }));

              await api.getConfigurator();
              window.stageApi = api;
              const player = window.playerApi || api.enableApi("player");
              window.playerApi = player;

              let configurator = await player.getConfigurator();
              // window.configurator = configurator;

              //Set product AssetId
              await configurator.setConfiguration({
                Asset: { assetId: styleAssetId },
              });

              //Get Asset attribute and then its Nested Configurator
              const assetAttribute = configurator
                ?.getDisplayAttributes()
                ?.find((attr) => attr?.name === "Asset");
              window.assetAttribute = assetAttribute;

              const assetNestedConfigurator =
                configurator.getNestedConfigurator(assetAttribute);
              // window.assetNestedConfigurator = assetNestedConfigurator;
              //Loop through every configuration property, get its nestedConfigurator and set it
              if (assetNestedConfigurator) {
                for (const propKey of Object.keys(initialConfiguration)) {
                  if (propKey) {
                    await assetNestedConfigurator.setConfiguration({
                      [propKey]: initialConfiguration[propKey],
                    });
                  }
                }
              }

              //Get again the updated global configurator
              configurator = assetNestedConfigurator;
              // window.configurator = assetNestedConfigurator;
              //Set global configurator
              commit("SET_GLOBAL_CONFIGURATOR", configurator);
              //Set configurator for decorations
              await dispatch("setDecorationsConfigurator");
              //Set decorations configurator (Only for a new 3D Product, not if the user is 'Editing' [it came from the locker clicking on the 'edit' button])

              await dispatch(
                "set3DPreviousDecorations",

                initialConfiguration
              );
              // : await dispatch("initDecorationsConfiguration");

              //Generate Decorations thumbnails (Only for previous decorations, because they are alreabe beeing set in the InitDecorationsConfiguration fn)

              await dispatch("updateSavedDecorationsThumbnails");

              if (isSublimated) {
                //Init the design Configurator
                await dispatch("initDesignConfigurator");

                //Init reversible design
                if (getters.isReversible(styleName)) {
                  await dispatch("initReversibleDesign");
                }
              }

              //Reset the reversible view to default (Outside)
              await dispatch("changeReversibleView", "Outside");

              if (!isSublimated) {
                //Get SKUS, and filter them by Size+Color
                const productPricing = await dispatch(
                  "project/getActiveProductPricing",
                  styleName,
                  {
                    root: true,
                  }
                );

                console.log({ productPricing });
              }
            } finally {
              await dispatch("setPlayerLoadingState", { loadingState: false });
            }
          });
        }
      } catch (error) {
        dispatch(
          "showToast",
          { msg: "Error loading 3D Player" },
          { root: true }
        );
        console.log(error);
      }
    },

    async resetThreekitPlayer({ commit }) {
      try {
        if (window.playerApi) {
          // window.playerApi && (await window.playerApi.unload());
          window.playerApi = null;
          commit("SET_GLOBAL_CONFIGURATOR", null);
          commit("SET_DESIGN_CONFIGURATOR", null);
          //Reset the view to Outside
          commit("SET_REVERSIBLE_VIEW", "Outside");
          //Reset decoration price
          commit("SET_DECORATIONS_PRICE", 0);
          commit("SET_COMPLETE_LOADER", true);
        }
      } catch (error) {
        console.log("error resetThreekitPlayer", error);
      }
    },

    //Decorations

    async initDecorationsConfiguration(
      { getters, dispatch },
      { decorationsBundleId }
    ) {
      try {
        let decorationsObject = {};

        const loopAndSetDecorations = async (isReversibleSide = false) => {
          //Some product seems to don't have decorations 🤔
          // if (state.decorationsConfigurator) {
          //Initialize product decorations
          const initial3DDecorations = await dispatch(
            "getInitial3DDecorations",
            isReversibleSide
          );
          if (initial3DDecorations)
            if (isReversibleSide) {
              decorationsObject["Decorations (Reverse)"] = {
                assetId: decorationsBundleId,
                configuration: { ...initial3DDecorations },
              };
            } else {
              decorationsObject["Decorations"] = {
                assetId: decorationsBundleId,
                configuration: { ...initial3DDecorations },
              };
            }
          /*
          We have to loop through an array of decoration Keys (ex: ['Left Chest', 'Lower Front']) 
          and set the configuration for each of them.
          */
          // for (const [index, locationName] of Object.keys(
          //   initial3DDecorations
          // ).entries()) {
          //   console.log({ index, locationName });
          //   await dispatch("set3DDecoration", {
          //     locationName,
          //     configuration: initial3DDecorations[locationName],
          //     setView: false,
          //     //Only get the decoration prices at the end of the loop
          //     skipGetDecorationPrices:
          //       index + 1 === Object.keys(initial3DDecorations).length
          //         ? false
          //         : true,
          //   });
          // }
          // //Generate Decorations thumbnails
          // await dispatch("updateSavedDecorationsThumbnails");
          // }
        };

        //Set outside decorations
        await loopAndSetDecorations();

        //If the product is reversible, set reversible decorations
        if (getters.isReversible3D) {
          // await dispatch("changeReversibleView", "Inside");
          await loopAndSetDecorations(true);
        }
        return decorationsObject;
      } catch (error) {
        console.log(error);
      }
    },

    async setDecorationsConfigurator({ state, getters, commit }) {
      const decorationKey = getters.decorationKey;
      //This configurator will be used to modify the decorations
      const configuratorAttribute = getters.getAttributeByName({
        configurator: state.globalConfigurator,
        attributeName: decorationKey,
      });
      //We have to check if the decorations configurator is already set
      if (!configuratorAttribute.value?.assetId) {
        const decorationsBundleAssetId =
          configuratorAttribute.values?.[0]?.assetId;
        await state.globalConfigurator.setConfiguration({
          [decorationKey]: { assetId: decorationsBundleAssetId },
        });
      }

      const configurator = getters.getNestedConfigurator({
        parentConfigurator: state.globalConfigurator,
        attributeName: decorationKey,
      });
      // window.decorationsConfigurator = configurator
      commit("SET_DECORATIONS_CONFIGURATOR", configurator);
    },

    async setActiveDecorationAttributes({ state, getters, dispatch, commit }) {
      await dispatch("clearActiveDecorationAttributes");
      console.log(
        "setActiveDecorationAttributes after clear",
        state.activeDecorationAttributes
      );
      if (!state.activeDecoration) return null;
      const locationName = state.activeDecoration?.locationName;

      const attributes = getters.getDecorationAttributes(locationName);

      commit("SET_ACTIVE_DECORATION_ATTRIBUTES", attributes);

      console.log(
        "setActiveDecorationAttributes after set",
        state.activeDecorationAttributes
      );
    },

    clearActiveDecorationAttributes({ commit }) {
      commit("SET_ACTIVE_DECORATION_ATTRIBUTES", null);
    },

    async getInitial3DDecorations(
      { rootState, state, getters, rootGetters },
      isReversibleSide
    ) {
      //Todo -> Temporaly we are generating the decorations based on the old Gameplan 1.5 products
      //We will receive the decorations based on the Threekit 2D Layout for each products/sports
      //todo take the team names and logos from the project configuration
      const teamName = rootGetters["project/projectTeamName"] || "Team Name";
      const defaultLogo = rootGetters["project/defaultLogo"];
      const projectColors = rootGetters["project/projectColors"];
      const isSublimated3D = getters["isSublimated3D"];

      //Get Threekit 3D Layout name
      let tkLayoutName = rootState.project?.activeProduct?.threekit_layout;
      let tkLayoutNameWithNumber = "";
      //For Uniforms layouts we use, for example, Football 1 or Football 2
      if (rootGetters["project/activeProductIsUniform"]) {
        //1 -> Only Logo
        //2 -> With team name + number
        //For snapsheets, always use the "2" option
        tkLayoutNameWithNumber = defaultLogo
          ? tkLayoutName + " 1"
          : tkLayoutName + " 2";
      }
      console.log({
        tkLayoutName,
        tkLayoutNameWithNumber,
        threekit3DLayouts: state.threekit3DLayouts,
      });

      const layout =
        state.threekit3DLayouts[tkLayoutNameWithNumber] ||
        state.threekit3DLayouts[tkLayoutName];

      if (!layout) {
        console.info("INIT 3D DECORATIONS: LAYOUT NOT FOUND");
        return null;
      }

      //Add Font and Palette
      layout.font = "FULL BLOCK";
      layout.colorPalette = isSublimated3D ? "sublimation" : "stock";

      //Decorations Colors: For the reversible side, use the Color 1 as the fill color
      const fillColor = isReversibleSide
        ? projectColors[2]?.color?.id
        : projectColors[1]?.color?.id;

      const outlineColor1 = isReversibleSide
        ? projectColors[0]?.color?.id
        : projectColors[2]?.color?.id;

      return rootGetters["decoration/initNew3DDecoration"]({
        layout,
        teamName,
        logo: defaultLogo,
        colorsPalette: isSublimated3D ? "sublimation" : "stock",
        fillColor,
        outlineColor1,
        font: layout.font,
      });
    },

    async getThreekit3DLayoutsList({ state, commit }) {
      if (!state.threekit3DLayouts) {
        try {
          const url = `${TK_ENV}api/datatables/${DATATABLE_PLP_TO_3D_LAYOUTS}/rows?orgId=${ORG_ID}&bearer_token=${AUTH_TOKEN}`;
          const options = {
            headers: {
              "Content-Type": "application/json",
            },
          };

          const { data } = await axios.get(url, options);

          const layouts = data?.rows?.reduce((acc, row) => {
            acc = {
              ...acc,
              [row?.value?.Layout]: row?.value,
            };
            //Add the first row
            if (!acc[acc?.value?.Layout]) {
              acc[acc?.value?.Layout] = acc?.value;
            }
            return acc;
          });
          console.log("SET_3D_LAYOUTS: ", layouts);
          commit("SET_3D_LAYOUTS", layouts);
        } catch (error) {
          console.log(error);
        }
      }
    },

    //Decorations Editor

    async set3DDecoration(
      { state, getters, dispatch, commit },
      {
        locationName,
        configuration,
        saveDecoration = true,
        generateThumbnail = false,
        setView = true,
        skipDecorationAllowedControl = false,
        skipGetDecorationPrices = false,
      }
    ) {
      console.log("configuration to apply", {
        locationName,
        configuration,
        skipGetDecorationPrices,
      });

      //Check if the location is allowed
      const activeDecorationAvailableLocationsByContentType =
        getters.activeDecorationAvailableLocationsByContentType;
      console.log(
        "activeDecorationAvailableLocationsByContentType",
        activeDecorationAvailableLocationsByContentType
      );
      const decorationIsAllowed = Object.keys(
        activeDecorationAvailableLocationsByContentType
      )?.some(() => {
        const typeOfDecoration =
          configuration?.["Content Type"] ||
          state.activeDecoration?.configuration?.["Content Type"];
        return activeDecorationAvailableLocationsByContentType[
          typeOfDecoration
        ]?.includes(locationName);
      });

      try {
        if (decorationIsAllowed || skipDecorationAllowedControl) {
          //Get the nested configurator of the decoration, so we can set the configuration
          const decorationNestedConfigurator = getters.getNestedConfigurator({
            parentConfigurator: state.decorationsConfigurator,
            attributeName: locationName,
          });

          //Set the configuration for the decoration
          await decorationNestedConfigurator.setConfiguration(configuration);
          await dispatch("setActiveDecorationAttributes");
          // -- Data we need to save: Material, Decoration shortcode, Color shortcode (if it's a text decoration)

          //Get the updated configuration
          const updatedDecorationNestedConfigurator =
            getters.getNestedConfigurator({
              parentConfigurator: state.decorationsConfigurator,
              attributeName: locationName,
            });

          const updatedAttributes =
            updatedDecorationNestedConfigurator.getDisplayAttributes();
          console.log({ updatedAttributes });
          //Get the material for the decoration
          const decorationMaterial = updatedAttributes?.find(
            (attr) => attr.name === "Decoration Material"
          );
          configuration["Decoration Material"] =
            decorationMaterial?.value || decorationMaterial?.defaultValue;

          //Get the location short code
          const decorationShortCode = updatedAttributes
            ?.find((attr) => attr.name === "Decoration Location Name")
            ?.value?.split(" ")
            ?.map((word) => word[0])
            ?.join("");
          configuration["Location Shortcode"] = decorationShortCode;

          //Get the location short code
          const decorationRotation = updatedAttributes
            ?.find((attr) => attr.name === "Rotation")
            ?.value?.split(" ")
            ?.map((word) => word[0])
            ?.join("");

          configuration["decorationRotation"] = decorationRotation;

          console.log("decorationRotation", decorationRotation, updatedAttributes)


          //Get the location view
          const decorationView = updatedAttributes?.find(
            (attr) => attr.name === "View"
          )?.value;
          configuration["view"] = decorationView;

          //Get the Height
          const decorationHeight = updatedAttributes?.find(
            (attr) => attr.name === "Text Height"
          )?.value;
          configuration["Text Height"] = decorationHeight;

          //Change the View (Front/Back/Sides)
          if (setView) {
            await dispatch("change3DView", { locationName });
          }

          //Set a thumbnail for a graphic decoration
          //For: new decorations that are logos
          //For: decorations that are being edited and are logos
          console.log("generateThumbnail", generateThumbnail);
          if (generateThumbnail) {
            await dispatch("updateActiveDecorationThumbnail");
          }

          //Get the colors shortcodes
          const textDecorationsTypes = [
            "Text Fill",
            "Text Outline 1",
            "Text Outline 2",
          ];
          const decorationIsText = textDecorationsTypes.some((textType) =>
            Object.keys(configuration)?.includes(textType)
          );
          console.log({ decorationIsText });

          if (decorationIsText) {
            //Colors
            const colorShortcodes = textDecorationsTypes.map((textType) => {
              const useBackup = false;
              const color = getters.findThreekitColorByAssetId(
                updatedAttributes?.find((attr) => attr?.name === textType)
                  ?.value?.assetId,
                useBackup
              );
              console.log("colorShortcodes color found: ", color);
              if (!color) configuration[textType] = null;
              const colorShortCode = color?.["metadata"]?.["Color Shortcode"];
              return colorShortCode;
            });
            configuration["colorCodes"] = colorShortcodes.filter(
              (color) => color
            );

            //Fonts
            const font = updatedAttributes?.find(
              (attr) => attr.name === "Font"
            )?.value;
            configuration["Font"] = font;
          }

          //Save the decorations on the state
          saveDecoration &&
            (await dispatch("updateDecorationThumbnail", locationName));
          saveDecoration &&
            commit("ADD_3D_DECORATION_TO_LIST", {
              locationName,
              configuration,
            });
          saveDecoration &&
            !getters.isSublimated3D &&
            !skipGetDecorationPrices &&
            (await dispatch("getDecorationPrices", {
              decorations: state.decorations3DList,
            }));
        }
      } catch (error) {
        console.log(error);
      }
    },

    async saveDecorationToList(
      { dispatch, commit },
      { locationName, fullConfiguration, reversibleView }
    ) {
      console.log({ fullConfiguration });
      try {
        const configuration = { ...fullConfiguration };
        //Get the material for the decoration
        configuration["Content Type"] = fullConfiguration["Content Type"];
        configuration["Decoration Material"] =
          fullConfiguration["Decoration Material"];

        //Get the location short code
        configuration["Location Shortcode"] =
          fullConfiguration["Location Shortcode"];

        //Get the location view
        configuration["view"] = fullConfiguration["View"];

        //Get the Height
        configuration["Text"] = fullConfiguration["Text"];
        configuration["Text Outline 1"] = fullConfiguration["Text Outline 1"];
        configuration["Text Outline 2"] = fullConfiguration["Text Outline 2"];
        configuration["Text Height"] = fullConfiguration["Text Height"];

        //Get the colors shortcodes
        // const textDecorationsTypes = [
        //   "Text Fill",
        //   "Text Outline 1",
        //   "Text Outline 2",
        // ];
        // const decorationIsText = textDecorationsTypes.some((textType) =>
        //   Object.keys(configuration)?.includes(textType)
        // );
        // const decorationHasText = configuration["Text"];

        //Text decorations config
        // if (decorationHasText) {
        //   //Font
        //   configuration["Font"] = fullConfiguration["Font"];

        //   //Colors
        //   const colorShortcodes = textDecorationsTypes.map((textType) => {
        //     const useBackup = false;
        //     const color = getters.findThreekitColorByAssetId(
        //       fullConfiguration[textType]?.assetId ||
        //         fullConfiguration[textType]?.value?.assetId,
        //       useBackup
        //     );
        //     if (!color) fullConfiguration[textType] = null;
        //     const colorShortCode = color?.["metadata"]?.["Color Shortcode"];
        //     return colorShortCode;
        //   });

        //   configuration["colorCodes"] = colorShortcodes.filter(
        //     (color) => color
        //   );
        // }

        //Save the decorations on the state

        //Check that the decoration is not empty
        if (configuration["Text"] || configuration["Logo"]?.["assetId"]) {
          commit("ADD_3D_DECORATION_TO_LIST", {
            locationName,
            configuration,
            reversibleView,
          });
          await dispatch("updateDecorationThumbnail", locationName);
        }
      } catch (error) {
        console.log(error);
      }
    },

    async set3DPreviousDecorations({ state, dispatch }, tkConfig) {
      try {
        //Save the decorations on the state
        const decorationsConfigs = [tkConfig["Decorations"]];
        if (tkConfig["Decorations (Reverse)"]) {
          decorationsConfigs.push(tkConfig["Decorations (Reverse)"]);
        }

        for (const [i, decorationsConfig] of decorationsConfigs.entries()) {
          let reversibleView = i === 0 ? "Outside" : "Inside";

          //Change the reversible view of the design
          await dispatch("changeReversibleView", reversibleView);

          for (const locationName of Object.keys(
            decorationsConfig.configuration
          )) {
            let decorationConfiguration = null;
            //Update some missing attributes
            const missingAttributes = await dispatch(
              "getMissingDecorationAttributes",
              locationName
            );
            console.log({ missingAttributes });
            if (missingAttributes) {
              decorationConfiguration = {
                ...decorationsConfig.configuration?.[locationName]
                  ?.configuration,
                ...missingAttributes,
              };
            }
            if (
              decorationsConfig.configuration?.[locationName]?.configuration
            ) {
              await dispatch("saveDecorationToList", {
                locationName: locationName,
                fullConfiguration:
                  decorationConfiguration ||
                  decorationsConfig.configuration?.[locationName]
                    ?.configuration,
                reversibleView,
              });
            }
          }
        }

        //Reset reversible view to Outside
        await dispatch("changeReversibleView", "Outside");
        console.log("POR LAS DUDAS: ", state.decorationsConfigurator);
        await dispatch("getDecorationPrices", {
          decorations: state.decorations3DList,
        });
      } catch (error) {
        console.log(error);
      }
    },

    async remove3DDecoration(
      { state, getters, dispatch, commit },
      locationName
    ) {
      try {
        await dispatch("setLoadingState", {
          loadingState: true,
          message: "Applying Changes...",
        });

        //Get the default configuration for the decoration
        const defaultDecorations =
          getters.getDecorationDefaultConfiguration(locationName);

        console.log("defaultDecorations", defaultDecorations);
        //Remove it from the 3D Player
        await dispatch("set3DDecoration", {
          locationName,
          configuration: defaultDecorations,
          setView: false,
          skipDecorationAllowedControl: true,
          skipGetDecorationPrices: true,
        });
        await dispatch("clearActiveDecorationAttributes");
        //Remove it from the state
        commit("REMOVE_3D_DECORATION_FROM_LIST", locationName);
        //Update decoration prices
        await dispatch("getDecorationPrices", {
          decorations: state.decorations3DList,
        });
      } catch (error) {
        console.log(error);
      } finally {
        dispatch("setLoadingState", { loadingState: false });
      }
    },

    async clear3DDecoration({ getters, dispatch }, locationName) {
      console.log("clear3DDecoration locationName", locationName);
      const defaultDecorations =
        getters.getDecorationDefaultConfiguration(locationName);
      await dispatch("clearActiveDecorationAttributes");
      await dispatch("set3DDecoration", {
        locationName,
        configuration: defaultDecorations,
        saveDecoration: false,
        setView: false,
        skipDecorationAllowedControl: true,
      });
    },

    async setActiveDecoration(
      { dispatch, commit },
      {
        locationName,
        configuration,
        saveOriginalDecorationLocation = false,
        isNewDecoration = false,
      }
    ) {
      try {
        //Set the active decoration
        commit("SET_ACTIVE_DECORATION", {
          locationName,
          configuration,
          saveOriginalDecorationLocation,
          isNewDecoration,
        });

        //Change the View (Front/Back/Sides)
        await dispatch("setActiveDecorationAttributes");
        await dispatch("change3DView", { locationName });
      } catch (error) {
        console.log(error);
      }
    },

    async setActiveDecorationLocation(
      { state, getters, dispatch },
      locationName
    ) {
      try {
        dispatch("setLoadingState", {
          loadingState: true,
          message: "Applying Changes...",
        });

        Vue.nextTick(async () => {
          try {
            //Clear the previous active decoration
            await dispatch(
              "clear3DDecoration",
              state.activeDecoration.locationName
            );

            //Update the active decoration location
            await dispatch("setActiveDecoration", {
              locationName,
              configuration: state.activeDecoration.configuration,
            });

            //Set the new active decoration and update the configurator
            await dispatch("set3DDecoration", {
              ...state.activeDecoration,
              saveDecoration: false,
            });

            //Fix Height
            //Get the updated configuration
            const updatedDecorationNestedConfigurator =
              getters.getNestedConfigurator({
                parentConfigurator: state.decorationsConfigurator,
                attributeName: locationName,
              });

            const heightAttribute = getters.getAttributeByName({
              configurator: updatedDecorationNestedConfigurator,
              attributeName: "Text Height",
            });

            await dispatch("setActiveDecorationChanges", {
              decorationPropertyName: "Text Height",
              decorationPropertyValue:
                heightAttribute?.values?.[heightAttribute?.values?.length - 1],
            });
          } catch (error) {
            console.log(error);
            dispatch("setLoadingState", { loadingState: false });
          } finally {
            dispatch("setLoadingState", { loadingState: false });
          }
        });
      } catch (error) {
        console.log(error);
        dispatch("setLoadingState", {
          loadingState: true,
          message: "Applying Changes...",
        });
      }
    },

    async setActiveDecorationChanges(
      { state, dispatch, commit },
      { decorationPropertyName, decorationPropertyValue }
    ) {
      try {
        const activeDecoration = cloneDeep(state.activeDecoration);
        activeDecoration.configuration[decorationPropertyName] =
          decorationPropertyValue;
        commit("SET_ACTIVE_DECORATION", activeDecoration);

        //Apply the decoration changes
        Vue.nextTick(async () => {
          await dispatch("set3DDecoration", {
            locationName: state.activeDecoration.locationName,
            configuration: activeDecoration.configuration,
            saveDecoration: false,
          });
        });
      } catch (error) {
        console.log(error);
      }
    },

    async saveActiveDecoration({ state, getters, dispatch, commit }) {
      await dispatch("updateActiveDecorationThumbnail");
      //First,checkif the location have changed
      if (
        state.activeDecoration?.originalDecorationLocation !==
        state.activeDecoration.locationName
      ) {
        console.log("CHANGE_3D_DECORATION_IN_LIST");
        //Remove the original decoration
        commit("CHANGE_3D_DECORATION_IN_LIST", state.activeDecoration);
      } else {
        console.log("ADD_3D_DECORATION_TO_LIST");
        commit("ADD_3D_DECORATION_TO_LIST", state.activeDecoration);
      }
      !getters.isSublimated3D &&
        (await dispatch("getDecorationPrices", {
          decorations: state.decorations3DList,
        }));

      commit("CLEAR_ACTIVE_DECORATION");
    },

    close3DDecorations({ commit }) {
      commit("CLEAR_ACTIVE_DECORATION");
      commit("CLEAR_3D_DECORATIONS_LIST");
    },

    async cancelActiveDecoration({ state, getters, dispatch, commit }) {
      //First we have to check if the active decoration changed its position
      if (
        state.activeDecoration?.originalDecorationLocation !==
        state.activeDecoration?.locationName
      ) {
        //If so, we have to undo that change, removing the logo and text values
        dispatch("clear3DDecoration", state.activeDecoration.locationName);
      }

      //Set the previous decoration

      const originalDecoration =
        getters.decorations3DListByReversibleView?.find(
          (decoration) =>
            decoration.locationName ===
            state.activeDecoration?.originalDecorationLocation
        );

      if (originalDecoration)
        await dispatch("set3DDecoration", {
          ...cloneDeep(originalDecoration),
          saveDecoration: false,
        });
      commit("CLEAR_ACTIVE_DECORATION");

      //Reset view
      await dispatch("change3DView", { view: "Front" });
      //Then we have to use the original decoration location, that is on the decoration 3d list
    },

    async createNew3DDecoration(
      { state, getters, rootGetters, dispatch },
      { typeOfDecoration, textType }
    ) {
      dispatch("setLoadingState", {
        loadingState: true,
        message: "Applying Changes...",
      });
      setTimeout(() => {
        Vue.nextTick(async () => {
          try {
            //Only run if we are already creating a new decoration or switching between decoration types (i.e. logo and text)
            if (
              state.activeDecoration
                ? state.activeDecoration.isNewDecoration
                : !state.activeDecoration
            ) {
              const colorsPalette = getters.isSublimated3D
                ? "sublimation"
                : "stock";
              //Clear the previous active decoration
              state.activeDecoration &&
                (await dispatch(
                  "clear3DDecoration",
                  state.activeDecoration.locationName
                ));

              //We need to checkthe type of decoration ('logo' or 'text')

              if (typeOfDecoration === "logo") {
                console.log("createNew3DDecoration logo");
                //Find the first available decoration location that matches with content type 'Graphics' or 'Uploads'
                const availableDecorationLocation =
                  getters.activeDecorationAvailableLocations([
                    "Uploads",
                    "Graphics",
                  ])[0];
                console.log(
                  "availableDecorationLocation",
                  availableDecorationLocation
                );
                await dispatch("setActiveDecoration", {
                  locationName: availableDecorationLocation.location,
                  configuration: {
                    Logo: { assetId: "" },
                    "Content Type": availableDecorationLocation.contentType,
                  },
                  saveOriginalDecorationLocation: false,
                  isNewDecoration: true,
                });

                //Set the new active decoration and update the configurator
                await dispatch("set3DDecoration", {
                  ...state.activeDecoration,
                  saveDecoration: false,
                  generateThumbnail: true,
                });
              } else {
                //Find the first available decoration locations that match with content type 'Text'
                const availableDecorationLocation =
                  getters.activeDecorationAvailableLocations([textType])[0];
                console.log(
                  "availableDecorationLocation",
                  availableDecorationLocation
                );
                const fillColor =
                  rootGetters["project/projectColors"][1]?.color?.id;
                const outlineColor1 =
                  rootGetters["project/projectColors"][2]?.color?.id;
                const fillBackupColor = "WHITE";
                const outlineBackupColor = "BLACK";

                await dispatch("setActiveDecoration", {
                  locationName: availableDecorationLocation.location,
                  configuration: {
                    Text: textType
                      ? textType === "Player Number"
                        ? "00"
                        : textType === "Player Name"
                        ? "Player Name"
                        : "Team Name"
                      : "",
                    "Content Type": availableDecorationLocation.contentType,
                    "Text Fill": {
                      assetId: rootGetters[
                        "threekit/getThreekitColorAssetIdByGameplanId"
                      ](fillColor, fillBackupColor, colorsPalette),
                    },
                    "Text Outline 1": {
                      assetId: rootGetters[
                        "threekit/getThreekitColorAssetIdByGameplanId"
                      ](outlineColor1, outlineBackupColor, colorsPalette),
                    },
                    "Text Outline 2": { assetId: "" },
                  },
                  saveOriginalDecorationLocation: false,
                  isNewDecoration: true,
                });

                //Set the new active decoration and update the configurator
                await dispatch("set3DDecoration", {
                  ...state.activeDecoration,
                  saveDecoration: false,
                });
              }
            }
          } catch (error) {
            console.log(error);
          } finally {
            dispatch("setLoadingState", { loadingState: false });
          }
        });
      }, 100);
    },

    async changeLogo({ state, dispatch }, logo) {
      try {
        const configuration = {
          Logo: { assetId: logo.asset_id, name: logo.filename, url: logo.url },
          "Content Type": logo.contentType || "Uploads",
        };

        if (state.activeDecoration) {
          const { locationName } = state.activeDecoration;

          await dispatch("setActiveDecoration", {
            locationName,
            configuration,
          });

          await dispatch("set3DDecoration", {
            locationName,
            configuration,
            saveDecoration: false,
            generateThumbnail: true,
          });
        }
      } catch (error) {
        console.log(error);
      }
    },

    //Get Decorations previews
    //upload/graphic: will return the original sized canvas that contains the maximum height non-rotated image
    //text content: will return a crop-to-fit image of text content (Text and Tail without tilt)
    async getDecorationThumbnail({ state, getters }, { player, locationName }) {
      const configuratorAttributes =
        state.globalConfigurator.getDisplayAttributes();
      const decorationsAttribute = configuratorAttributes.find(
        (attribute) => attribute.name === getters.decorationKey
      );

      const locationsBundle_config =
        state.globalConfigurator.getNestedConfigurator(decorationsAttribute);
      const attrArr = locationsBundle_config.getDisplayAttributes();
      const theLocation = attrArr.find((i) => {
        return i.name == locationName;
      });

      if (!theLocation) return null;

      const location_config =
        locationsBundle_config.getNestedConfigurator(theLocation);

      const decorImgSnapshot_obj = configuratorAttributes.find(
        (i) => i.name == "Decoration Image Snapshot" && i.type == "Asset"
      );

      if (!decorImgSnapshot_obj) return null;

      const decorImgSnapshot_config =
        state.globalConfigurator.getNestedConfigurator(decorImgSnapshot_obj);

      if (!decorImgSnapshot_config) return null;

      const rootId = window.stageApi.scene.get([
        window.stageApi.instanceId,
        "eval",
        "Proxy",
        "proxy",
      ]).id;

      const matId = await window.stageApi.scene.addNode(
        {
          type: "Material",
          name: "tmpnode",
          plugs: {
            Material: [
              {
                type: "Physical",
                baseMap: decorImgSnapshot_config.inputs.id,
              },
            ],
          },
        },
        rootId
      );

      await decorImgSnapshot_config.setConfiguration(
        //location_config.getConfiguration()
        location_config.appliedConfiguration
      );

      await window.stageApi.when("rendered");

      const snapshotInsId = await player.getAssetInstance({
        id: decorImgSnapshot_config.inputs.id,
        plug: "Proxy",
        property: "asset",
      });

      const assetConfigurator = window.stageApi.scene.get([
        snapshotInsId,
        "eval",
        "configurator",
      ]);

      await assetConfigurator.setConfiguration({
        randomNumber: Math.random() * 100000,
      });

      let img = window.stageApi.scene.getImage(snapshotInsId);

      const txtDestRect = window.stageApi.scene.get([
        snapshotInsId,
        "plugs",
        "Image",
        10,
      ]).destRect;

      let snapShotWidth = img.width;
      let snapShotX = 0;
      let snapshotHeight = img.height;

      if (txtDestRect) {
        snapShotWidth = txtDestRect.right - txtDestRect.left;
        snapShotX = txtDestRect.left - snapShotWidth * 0.1; // give 10% padding
        snapShotWidth *= 1.3; //give padding for tail and extra space
      }

      if (
        location_config.appliedConfiguration["Text Effect"] ==
          "Text and Tail" ||
        location_config.appliedConfiguration["Text Effect"] ==
          "Text and Tail Straight"
      )
        snapshotHeight = (snapshotHeight / 1.6) * 1.5;
      img = window.stageApi.scene.getImage(snapshotInsId, {
        rect: {
          x: snapShotX,
          y: 0,
          width: snapShotWidth,
          height: snapshotHeight,
        },
      });
      await window.stageApi.scene.deleteNode(matId);
      return img;
    },

    async updateSavedDecorationsThumbnails({ state, dispatch }) {
      try {
        //This will update the decorations thumbnails based on a given decoration list
        for (const decoration of state.decorations3DList) {
          await dispatch("updateDecorationThumbnail", decoration.locationName);
        }
      } catch (error) {
        console.log(error);
      }
    },

    async updateActiveDecorationThumbnail({ state, dispatch, commit }) {
      try {
        //This will update the decorations thumbnails based on a given decoration list
        const { locationName } = state.activeDecoration;
        const decorationThumbnail = await dispatch("getDecorationThumbnail", {
          player: window.playerApi,
          locationName,
        });
        if (decorationThumbnail) {
          commit(
            "SET_ACTIVE_DECORATION_THUMBNAIL",
            decorationThumbnail?.toDataURL()
          );
        }
      } catch (error) {
        console.log(error);
      }
    },

    async updateDecorationThumbnail(
      { state, getters, dispatch, commit },
      locationName
    ) {
      try {
        //This will update the decorations thumbnails based on a given decoration list
        //Get the View (Front/Back/Sides)
        const decorationConfigurator = getters.getNestedConfigurator({
          parentConfigurator: state.decorationsConfigurator,
          attributeName: locationName,
        });

        const attributes = decorationConfigurator.getDisplayAttributes();
        const viewAttribute = attributes.find((i) => i.name == "View");

        const decorationThumbnail = await dispatch("getDecorationThumbnail", {
          player: window.playerApi,
          locationName,
        });

        if (decorationThumbnail) {
          commit("SET_3D_DECORATION_THUMBNAIL", {
            thumbnail: decorationThumbnail.toDataURL(),
            locationName,
            view: viewAttribute.value,
          });
        }
      } catch (error) {
        console.log(error);
      }
    },

    getMissingDecorationAttributes({ state, getters }, locationName) {
      if (!state.decorationsConfigurator) return null;

      //Check if the location is allowed
      const decorationsAttributes =
        state.decorationsConfigurator.getDisplayAttributes();
      if (
        !decorationsAttributes.some((decorationAttr) => {
          console.log({
            "decorationAttr name": decorationAttr?.name,
            locationName,
          });
          return decorationAttr?.name === locationName;
        })
      ) {
        return null;
      }
      //Get the updated configuration
      const updatedDecorationNestedConfigurator = getters.getNestedConfigurator(
        {
          parentConfigurator: state.decorationsConfigurator,
          attributeName: locationName,
        }
      );

      if (!updatedDecorationNestedConfigurator) return null;

      const updatedAttributes =
        updatedDecorationNestedConfigurator.getDisplayAttributes();
      console.log({});
      //Get the material for the decoration
      const decorationMaterial = updatedAttributes?.find(
        (attr) => attr.name === "Decoration Material"
      );
      // configuration["Decoration Material"] =
      //   decorationMaterial?.value || decorationMaterial?.defaultValue;

      //Get the location short code
      const decorationShortCode = updatedAttributes
        ?.find((attr) => attr.name === "Decoration Location Name")
        ?.value?.split(" ")
        ?.map((word) => word[0])
        ?.join("");

      //Get the colors shortcodes
      const textDecorationsTypes = [
        "Text Fill",
        "Text Outline 1",
        "Text Outline 2",
      ];
      const decorationIsText = textDecorationsTypes.some((textType) =>
        updatedAttributes?.map((attr) => attr?.name)?.includes(textType)
      );
      console.log({ decorationIsText });
      let colorShortcodes = [];
      if (decorationIsText) {
        //Colors
        colorShortcodes = textDecorationsTypes
          .map((textType) => {
            const useBackup = false;
            const color = getters.findThreekitColorByAssetId(
              updatedAttributes?.find((attr) => attr?.name === textType)?.value
                ?.assetId,
              useBackup
            );
            console.log("colorShortcodes color found: ", color);
            // if (!color) configuration[textType] = null;
            const colorShortCode = color?.["metadata"]?.["Color Shortcode"];
            return colorShortCode;
          })
          ?.filter((color) => color);
      }

      const newAttributes = {
        colorCodes: colorShortcodes,
        "Decoration Material": decorationMaterial?.value,
        "Location Shortcode": decorationShortCode,
      };

      return newAttributes;
    },

    async getDecorationPrices(
      { rootState, commit },
      { decorations, quantity }
    ) {
      console.log({ "decorations getDecorationPrices": decorations });
      try {
        const decorationsData = {
          entities: [],
          quantity: quantity || 1, // product quantity, always 1 when the user adds a product to the locker,
          // fsg_code: rootState.auth.dealerMode
          //   ? rootState.dealer.fsg_code
          //   : null,
        };

        if (decorations.length > 0) {
          decorations.forEach((decoration) => {
            const view = decoration.view;
            const locationCode = decoration.configuration["Location Shortcode"];
            const entityType = decoration.configuration["Content Type"];
            const decorationMethod =
              decoration.configuration["Decoration Material"];
            const colorCodes = decoration.configuration?.["colorCodes"];
            const numDecorationColors = colorCodes?.length;

            const decorationData = {
              colorCodes,
              decorationMethod,
              locationCode,
              numDecorationColors:
                numDecorationColors > 0 ? numDecorationColors : 1,
              entityType,
              view,
            };

            //When the entityType is empty, it means that the decoration is empty
            if (entityType) decorationsData.entities.push(decorationData);
          });

          const url =
            rootState.settings.endpoint + `api/v2/lockers/decorations`;

          const options = {
            headers: {
              "Content-Type": "application/json",
            },
            params: decorationsData,
          };
          console.log("decorationsData", decorationsData);
          const { data } = await axios.get(url, options);
          const price = data?.reduce((acc, curr) => {
            //cents to dollars
            return acc + curr / 100;
          }, 0);

          commit("SET_DECORATIONS_PRICE", price);

          //Update prices for each decoration
          const updatedDecorations = decorations?.map((decoration, i) => {
            return { ...decoration, price: data[i] / 100 };
          });
          console.log({ updatedDecorations });
          commit("SET_3D_DECORATIONS_LIST", updatedDecorations);
          return price;
        } else {
          commit("SET_DECORATIONS_PRICE", 0);
          return 0;
        }
      } catch (error) {
        console.log(error);
      }
    },

    async getColorsByMaterial({ commit }) {
      const materialTypes = ["duratek", "silicone", "plastisol", "sublimation"];
      for (let index = 0; index < materialTypes.length; index++) {
        const material = materialTypes[index];

        const url = `${TK_ENV}${TK_ASSET_API}?bearer_token=${AUTH_TOKEN}&tags=${material}&all=true`;

        console.log("MATERIAL URL", url);

        const response = await axios.get(url);

        const materialColors = response.data?.assets;

        commit("SET_MATERIAL_COLORS", {
          materialType: material,
          colors: materialColors,
        });

        console.log("set material color", {
          materialType: material,
          colors: materialColors,
        });
      }
    },

    //2D Decorations

    save2DDecoration({ commit }, decoration) {
      commit("ADD_2D_DECORATION_TO_LIST", decoration);
    },

    //Designs

    async initDesignConfigurator({ dispatch }) {
      //We need to 'restart' the design configurator and the available colors, due a vuex limitation.
      //If we use the designConfigurator and AvailabelColors as getters, Vuex will not update the values since its source of data is globalCofigurator, and that data doesn't change.
      //(I've tried to 'reset' the globalConfigurator, but it doesn't work 😪)
      await dispatch("setDesignConfigurator");
      //Change color mode from 'Layer' to 'Group'
      await dispatch("setDesignColorModeToGroup");
    },

    async initReversibleDesign({ state, getters }) {
      const configurator = getters.getNestedConfigurator({
        parentConfigurator: state.globalConfigurator,
        attributeName: "Design (Reverse)",
      });

      await configurator.setConfiguration({ "Color Mode": "Group" });
    },

    setDesignConfigurator({ state, getters, commit }) {
      //This configurator will be used to modify the design and the colors/variant color of the product
      const designKey = getters.designKey;
      console.log("designKey", designKey);
      const configurator = getters.getNestedConfigurator({
        parentConfigurator: state.globalConfigurator,
        attributeName: designKey,
      });

      commit("SET_DESIGN_CONFIGURATOR", configurator);
    },

    async changeReversibleView(
      { state, getters, dispatch, commit },
      reversibleView
    ) {
      try {
        commit("SET_REVERSIBLE_VIEW", reversibleView);
        //Change the reversible view of the design
        await state.globalConfigurator.setConfiguration({
          "Reversible View": reversibleView,
        });
        //Update the design and decoration configurators
        getters.isSublimated3D && (await dispatch("initDesignConfigurator"));
        await dispatch("setDecorationsConfigurator");
      } catch (error) {
        console.log(error);
      }
    },

    async changeDesign({ state, getters, dispatch }, designId) {
      if (!state.playerLoader) {
        try {
          await dispatch("setLoadingState", {
            loadingState: true,
            message: "Applying Changes...",
          });
          //We will only send the colors that are filled as parameters, otherwise we would get a bugged layer
          const filledDesignColors = getters.designColors.filter((c) => {
            return c && c.color && c.color?.name;
          });

          const config = getters.getColorsAndDesignConfiguration({
            designId,
            styleName: getters["activeStyleName"],
            colors: filledDesignColors,
          });
          console.log("config", config);

          await state.globalConfigurator.setConfiguration({
            Design: config["Design"],
          });
          //If it is reversible, change the view to the Inside so we can set the reversible colors
          if (getters.isReversible3D) {
            await dispatch("changeReversibleView", "Inside");

            //Loop through the colors and set the colors
            // for (const colorKey of config['Design (Reverse)']?.configuration) {
            //   await dispatch('changeDesignColor', { colorName: colorKey, assetId: config['Design (Reverse)']?.configuration[colorKey]?.assetId })
            // }
          }
          await dispatch("changeReversibleView", "Outside");
        } catch (error) {
          console.log(error);
        } finally {
          await dispatch("setLoadingState", { loadingState: false });
        }
      }
    },

    async changeStockVariant({ state, dispatch }, assetId) {
      console.log("assetId", assetId);
      try {
        await dispatch("setLoadingState", {
          loadingState: true,
          message: "Applying Changes...",
        });
        await state.globalConfigurator.setConfiguration({
          Color: {
            assetId,
          },
        });
      } catch (error) {
        console.log(error);
      } finally {
        await dispatch("setLoadingState", { loadingState: false });
      }
    },

    async setDesignColorModeToGroup({ state }) {
      await state.designConfigurator.setConfiguration({
        "Color Mode": "Group",
      });
    },

    async handleChangeDesignColor({ state, getters, dispatch }, selectedColor) {
      if (!state.playerLoader) {
        await dispatch("setLoadingState", {
          loadingState: true,
          message: "Applying Changes...",
        });
        const colorKey = getters.getThreekitColorKey(selectedColor.colorKey);

        Vue.nextTick(async () => {
          setTimeout(async () => {
            try {
              await dispatch("changeDesignColor", {
                colorName: colorKey,
                assetId: selectedColor.color.assetId,
              });
            } catch (error) {
              console.log(error);
            } finally {
              await dispatch("setLoadingState", { loadingState: false });
            }
          }, 100);
        });
      }
    },

    async changeDesignColor(
      { state },
      { colorName, assetId, multiple = false, params }
    ) {
      //Set mulitple colors in one call, mostly usde to update the colors when the design has been changed
      if (multiple) {
        await state.designConfigurator.setConfiguration(params);
      } else {
        //Individual color, mostly used on color picker event
        await state.designConfigurator.setConfiguration({
          [colorName]: { assetId },
        });
      }
    },

    //Fabrics

    async changeFabric({ state, dispatch }, assetId) {
      try {
        dispatch("setLoadingState", {
          loadingState: true,
          message: "Applying Changes...",
        });
        await state.globalConfigurator.setConfiguration({
          Fabric: {
            assetId,
          },
        });
      } catch (error) {
        console.log(error);
      } finally {
        dispatch("setLoadingState", { loadingState: false });
      }
    },

    //Get product information (designs, layers, etc)
    async initProductCache(
      { state, getters, dispatch, commit },
      { styleName, designName }
    ) {
      styleName = styleName ? styleName?.toUpperCase() : "";
      designName = designName ? designName?.toUpperCase() : "";
      //Get Sublimation and Stock colors
      await dispatch("initThreekitColors");

      //Get Fonts
      await dispatch("getThreekitFonts");

      //Set Default Uniform and Spiritwear font
      await dispatch("project/setDefaultDecorationFonts", null, { root: true });

      try {
        //Get the product info
        if (!state.cachedTKData.products[styleName]) {
          //Set empty cache to avoid unnecessary calls
          commit("SET_PRODUCT_CACHE", {
            styleName,
            payload: {
              item: {},
              designs: {},
            },
          });

          const productInfo = await dispatch("getAssetList", {
            tag: `["type_style","style_${styleName}"]`,
          });

          if (!productInfo?.[0] || !productInfo) return null;

          commit("SET_PRODUCT_CACHE", {
            styleName,
            payload: {
              item: productInfo[0].product,
              designs: state.cachedTKData.products[styleName]?.designs || {},
            },
          });
        }

        //Check if the product is Sublimated
        const isSublimated = getters["isSublimated"](styleName);

        //Get the design and the layers
        if (
          isSublimated &&
          !state.cachedTKData.products[styleName].designs[designName]
        ) {
          //Set empty cache to avoid unneccessary calls

          commit("SET_PRODUCT_CACHE_SUBSTATE", {
            styleName,
            parentState: "designs",
            subState: `${designName}`,
            payload: {
              item: {},
              layers: [],
            },
          });

          const designs = await dispatch("getAssetList", {
            tag: `["type_design","style_${styleName}","design_${designName}"]`,
          });

          const layers = await dispatch("getAssetList", {
            tag: `["type_layer","style_${styleName}","design_${designName}"]`,
            full: false,
          });

          if (!designs[0]) return null;

          commit("SET_PRODUCT_CACHE_SUBSTATE", {
            styleName,
            parentState: "designs",
            subState: `${designName}`,
            payload: {
              item: designs[0].product,
              layers: layers.assets,
            },
          });

          const designItem =
            state.cachedTKData.products[styleName].designs[designName].item;
          //Implement new logic for cached Layer Attributes
          const layerAttributes = designItem.attributes.filter(
            (attr) =>
              attr.name.startsWith("Layer -") && !attr.name.includes("Logo")
          );
          for (const attr of layerAttributes) {
            const layerItem = await dispatch(
              "getFullAssetbyId",
              attr.defaultValue.assetId
            );
            // console.log({
            //   [`getFullAssetbyId ${styleName} ${designName}`]: layerItem,
            // });
            const orderMetadata = layerItem.metadata.find(
              (entry) => entry.name === "Order"
            );
            const colorGroupMetadata = layerItem.metadata.find(
              (entry) => entry.name === "Color Group"
            );
            attr.Order = orderMetadata?.defaultValue;
            attr.colorGroup = colorGroupMetadata?.defaultValue;
          }
          layerAttributes.sort((a, b) => {
            const orderA = parseInt(a.Order);
            const orderB = parseInt(b.Order);
            return orderA - orderB;
          });

          commit("SET_PRODUCT_CACHE_SUBSTATE", {
            styleName,
            parentState: "designs",
            subState: `${designName}`,
            payload: {
              layerAttributes,
            },
          });
        }

        //Get the stock asset
        if (!isSublimated && !state.cachedTKData.products[styleName].stock) {
          const stockColors = await dispatch("getAssetList", {
            tag: `["type_stock-color","style_${styleName}"]`,
            full: false,
          });

          commit("SET_PRODUCT_CACHE_STATE", {
            styleName,
            subState: "stock",
            payload: stockColors.assets,
          });
        }
      } catch (error) {
        console.log("initProductCache styleName designName", {
          styleName,
          designName,
        });
        console.log(`${error} in ${styleName} ${designName} `);
      }
    },

    async getAssetList(_, { tag, full = true }) {
      try {
        const url = `${TK_ENV}${
          full ? TK_FULLASSET_API : TK_ASSET_API
        }?orgId=${ORG_ID}&bearer_token=${AUTH_TOKEN}${
          tag ? `&tags=${encodeURIComponent(tag)}` : ""
        }${full ? "&all=true" : "&perPage=100"}`;

        const { data } = await axios.get(url);
        return data;
      } catch (error) {
        console.log("FAILED GET ASSETLIST", error);
      }
    },

    getFullAssetbyId(_, id) {
      const url = `${TK_ENV}${TK_ASSET_API}/export/${id}?orgId=${ORG_ID}&bearer_token=${AUTH_TOKEN}`;
      return fetch(url).then((res) => res.json());
    },

    async initThreekitColors({ state, getters, dispatch, commit }) {
      try {
        const getColors = async (colorType) => {
          const type = colorType.toLowerCase();
          const colorKey =
            colorType === "sublimation" ? "sublimationColors" : "stockColors";
          if (!["sublimation", "stock-print"].includes(type))
            throw new Error("colorType must be sublimation or stock-print");

          //Get sublimations colors
          if (!state[colorKey].length) {
            const colors = await dispatch("getAssetList", {
              tag: `type_${type}-color`,
              full: false,
            });
            //Map the corresponding colors. Todo check if we have to differentiate between sublimation/stock/chromagear/etc
            const mappedColors = getters.mapThreekitColorWithConstantColors(
              colors.assets,
              type
            );

            //Save them
            commit("SET_COLORS", { type: colorKey, colors: mappedColors });
          }
        };

        await getColors("sublimation");
        await getColors("stock-print");
      } catch (error) {
        console.log(error);
      }
    },

    async getDataTable(_, datatableId) {
      try {
        const datatableUrl = `${TK_ENV}api/datatables/${datatableId}/rows?orgId=${ORG_ID}&bearer_token=${AUTH_TOKEN}&all=true`;
        const { data } = await axios.get(datatableUrl);
        return data;
      } catch (error) {
        console.log(error);
      }
    },

    processLocation(_, locationTable) {
      return locationTable.rows.reduce((acc, row) => {
        const name = row.value["Location Name"];
        delete row.value["Location Name"];
        const { Name, Graphic, Number, ...configuration } = row.value;
        acc[name] = { configuration, contentTypes: { Name, Graphic, Number } };
        return acc;
      }, {});
    },

    async fetchLocationsTable({ dispatch, commit }) {
      const locationDataTable = await dispatch("getDataTable", DATATABLE_ID);
      const dataTable = await dispatch("processLocation", locationDataTable);
      commit("SET_DATA_TABLE", { dataTableId: DATATABLE_ID, dataTable });
    },

    async appendCustomLocationsTable({ dispatch, commit }, hardcodedLocations) {
      const locationDataTable = await dispatch("getDataTable", DATATABLE_ID);
      const dataTable = await dispatch("processLocation", locationDataTable);
      commit("SET_DATA_TABLE", {
        dataTableId: DATATABLE_ID,
        dataTable: { ...dataTable, hardcodedLocations },
      });
    },

    /**
     * This function returns all colors of a colorType when no colorName is provided,
     * otherwise it returns the colorName item. colorType is sublimation or stock-print
     * Use the color item's id as the assetId for Name Fill, Name Outline, Number Fill, Number Outline
     * */

    async getColors({ state, dispatch }, { colorType, colorName }) {
      const type = colorType.toLowerCase();
      const colorKey =
        colorType === "sublimation" ? "sublimationColors" : "stockColors";

      if (!["sublimation", "stock-print"].includes(type))
        throw new Error("colorType must be sublimation or stock-print");

      if (!state.cachedTKData[colorKey].length) {
        const colors = await dispatch("getAssetList", {
          tag: `type_${type}-color`,
          full: false,
        });
        return colors;
      } else {
        return state.cachedTKData[type].find(
          (item) => item.name === colorName?.toUpperCase()
        );
      }
    },

    /**
     * This function returns all fonts when no fontName is provided,
     * otherwise it returns the fontName item.
     * Use the font item's id as the assetId for Font
     * */

    async getThreekitFonts({ state, dispatch, commit }) {
      if (!state.fonts.length && !state.threeekitFontsIsLoading) {
        commit("SET_THREEKIT_FONTS_IS_LOADING");
        const fonts = await dispatch("getAssetList", {
          tag: `type_font`,
          full: false,
        });
        commit("SET_FONTS", fonts.assets);
      }
    },

    /**
     * This function returns all data from the PLP decoration locations datatable when no locationName is provided,
     * otherwise it returns the locationName information.
     * It is used internally, but it can also be used to see what locations are available
     * */

    async getLocations({ state, dispatch }, locationName) {
      if (!state.cachedTKData.datatables[DATATABLE_ID]) {
        await dispatch("fetchLocationsTable");
      }
      if (!locationName) return state.cachedTKData.datatables[DATATABLE_ID];
      return state.cachedTKData.datatables[DATATABLE_ID][locationName];
    },

    //returns whole config, so we can get the field "Type" which tells if location is uniform or spiritwear
    async getConfigurationByLocationName({ dispatch }, locationName) {
      const location = await dispatch("getLocations", locationName);
      if (location != null) {
        return location.configuration;
      }
      return null;
    },

    getUniformDecorationConfiguration(
      { getters, rootGetters },
      locationConfiguration
    ) {
      if (!locationConfiguration) return false;

      const fillColor = rootGetters["project/uniformFillColor"],
        outlineColor = rootGetters["project/uniformOutlineColor"],
        fillBackupColor = "WHITE",
        outlineBackupColor = "BLACK",
        graphic = rootGetters["project/isShowroom"]
          ? rootGetters["uploadLogos/editableLogo"]
          : rootGetters["project/defaultLogo"];
      //const colorsPalette = getters.isSublimated3D ? 'sublimation' : 'stock'
      const colorsPalette = "sublimation"; //TODO
      const hasGraphic =
        (graphic && graphic.asset_id) || (graphic && graphic.assetId);
      const graphicAssetId =
        hasGraphic &&
        (graphic.asset_id != null ? graphic.asset_id : graphic.assetId);

      let conf = {
        Number: "23",
        "Number Fill": {
          assetId: getters["getThreekitColorAssetIdByGameplanIdShowroom"](
            fillColor,
            fillBackupColor,
            colorsPalette
          ),
        },
        "Number Outline": {
          assetId: getters["getThreekitColorAssetIdByGameplanIdShowroom"](
            outlineColor,
            outlineBackupColor,
            colorsPalette
          ),
        },
        Name: rootGetters["project/uniformMascot"]?.toUpperCase(),
        "Name Fill": {
          assetId: getters["getThreekitColorAssetIdByGameplanIdShowroom"](
            fillColor,
            fillBackupColor,
            colorsPalette
          ),
        },
        "Name Outline": {
          assetId: getters["getThreekitColorAssetIdByGameplanIdShowroom"](
            outlineColor,
            outlineBackupColor,
            colorsPalette
          ),
        },
        "Name Height": locationConfiguration["Name Height"],
        "Name Max Width": locationConfiguration["Name Max Width"],
        //"Name Height": "50",
        //"Name Max Width": "200",
        Font: {
          assetId: rootGetters["project/uniformFont"]?.id,
        },
        Graphic: { assetId: graphicAssetId ? graphicAssetId : "" },
        "Graphic Height": locationConfiguration["Graphic Height"],
        "Graphic Width": locationConfiguration["Graphic Width"],
        "Graphic X Offset": locationConfiguration["Graphic X Offset"],
        "Graphic Y Offset": locationConfiguration["Graphic Y Offset"],
      };

      return conf;
    },

    /*
    async getSpiritwearDecorationConfiguration(
      { getters, rootGetters },
      locationName
    ) {
      const fillColor = rootGetters["project/spiritwearFillColor"],
        outlineColor = rootGetters["project/spiritwearOutlineColor"],
        fillBackupColor = "WHITE",
        outlineBackupColor = "BLACK";

      //const colorsPalette = getters.isSublimated3D ? 'sublimation' : 'stock'
      const colorsPalette = "sublimation"; //TODO

      const locationIsSpiritwear =
        getters["locationNameIsSpiritwear"](locationName);
      const locationInSports =
        getters["spiritwearLocationInSports"](locationName);

      const centerDecorationAssetId = //"dddcd792-4e8b-4536-b193-eac26dc9df04"
        locationInSports.length > 1
          ? ""
          : getters["getCenterDecorationBySport"](locationInSports[0]); // '' should be the default id

      //      console.log('centerDecorationAssetId', centerDecorationAssetId)
      //if we have more than 1 sport using this location, then we set athletics, else sportname
      const bottomText =
        rootGetters["project/spiritwearSubMascot"] !== "ATHLETICS"
          ? rootGetters["project/spiritwearSubMascot"]
          : locationIsSpiritwear &&
            locationInSports != null &&
            locationInSports.length > 1
            ? rootGetters["project/spiritwearSubMascot"]
            : locationInSports[0];

      return {
        "Top Text": rootGetters["project/spiritwearMascot"]?.toUpperCase(),
        "Top Color": {
          assetId: getters["getThreekitColorAssetIdByGameplanId"](
            fillColor,
            fillBackupColor,
            colorsPalette
          ),
        },
        "Top Outline 1": {
          assetId: getters["getThreekitColorAssetIdByGameplanId"](
            outlineColor,
            outlineBackupColor,
            colorsPalette
          ),
        },

        "Middle Logo": {
          assetId: centerDecorationAssetId,
          configuration: {
            Fill: {
              assetId: getters["getThreekitColorAssetIdByGameplanId"](
                fillColor,
                fillBackupColor,
                colorsPalette
              ),
            },
          },
        },

        "Bottom Color": {
          assetId: getters["getThreekitColorAssetIdByGameplanId"](
            fillColor,
            fillBackupColor,
            colorsPalette
          ),
        },
        "Bottom Outline 1": {
          assetId: getters["getThreekitColorAssetIdByGameplanId"](
            outlineColor,
            outlineBackupColor,
            colorsPalette
          ),
        },
        "Bottom Text": bottomText,
        "Top Font": { assetId: rootGetters["project/spiritwearFont"].id },
        "Bottom Font": { assetId: rootGetters["project/spiritwearFont"].id },
      };
    },*/

    async getSpiritwearThumbnail({ commit, rootGetters }) {
      const graphic = rootGetters["project/spiritwearCustomGraphic"];
      if (graphic) {
        commit("SET_SPIRITWEAR_THUMBNAIL", graphic.url);
        return graphic.url;
      }

      const api = window.decorationsApi;
      const myplayer = api.enableApi("player");

      if (myplayer.configurator.appliedConfiguration.Graphic) {
        const graphicAssetIns = await myplayer.getAssetInstance({
          id: myplayer.configurator.appliedConfiguration.Graphic,
          plug: "Proxy",
          property: "asset",
        });

        const canvas = await api.scene.getImage(graphicAssetIns);
        console.log({
          "graphicAssetIns spiritwear": graphicAssetIns,
          "canvas spiritwear": canvas,
        });
        commit("SET_SPIRITWEAR_THUMBNAIL", canvas.toDataURL());
      } else {
        console.log(
          "ERROR: missing myplayer.configurator.appliedConfiguration.Graphic"
        );
      }
    },

    async getUniformThumbnail({ state, commit, rootGetters }, locationName) {
      const graphic = rootGetters["project/isSnapsheet"]
        ? null
        : rootGetters["project/uniformCustomGraphic"];

      if (graphic) {
        commit("SET_UNIFORM_THUMBNAIL", graphic.url);
        return graphic.url;
      }

      console.log("THUMB location conf for " + locationName);
      console.log(
        state.cachedTKData.datatables[DATATABLE_ID][locationName].configuration
      );

      //const locationConf = state.cachedTKData.datatables[DATATABLE_ID][locationName].configuration

      const api = window.decorationsApi;
      const myplayer = api.enableApi("player");

      const canvasSnapshotIns = await myplayer.getAssetInstance({
        id: api.instanceId,
        plug: "Proxy",
        property: "asset",
      });
      const canvasSnapshotNode = api.scene.get({ id: canvasSnapshotIns });
      const canvasTextureId = canvasSnapshotNode.configurator.attributes.find(
        (item) => item.name == "Canvas Texture"
      ).id;

      const canvasTextureAssetId =
        canvasSnapshotNode.configurator.attributes.find(
          (item) => item.name == "Canvas Texture"
        ).defaultValue.assetId;
      const decorPLPNodeId = await myplayer.sceneGraph.assetReferences.from[
        canvasTextureAssetId
      ][
        `["${canvasSnapshotIns}","configurator","attributes","${canvasTextureId}"]`
      ];
      const img = await api.scene.getImage(decorPLPNodeId, {
        rect: {
          x: 560,
          y: 605,
          width: 800,
          height: 230,
        },
      });

      commit("SET_UNIFORM_THUMBNAIL", img.toDataURL());

      return img.toDataURL();
      /*const player = window.api.enableApi("player");
      const configuratorAttributes = player.configurator.getDisplayAttributes();

      console.log('configuratorAttributes', configuratorAttributes)
      const nameConf = configuratorAttributes.find(
        (i) => i.name == "Name" && i.type == "String"
      );

      console.log('decorImgSnapshot_obj', nameConf)

      
      let img = window.api.scene.getImage(nameConf.id);

      console.log(img.toDataURL())
*/

      /*const api = window.api
      const myplayer = api.enableApi('player')
      console.log('applied conf for uni: ', myplayer.configurator.appliedConfiguration)
      const graphicAssetIns = await myplayer.getAssetInstance({id: myplayer.configurator.appliedConfiguration, plug: 'Proxy', property: 'asset' });
      const img = await api.scene.getImage(graphicAssetIns, {
        rect: { x: 0, y: 380, width: 512, height: 120 }
        } );
      
      //const canvas = await api.scene.getImage(graphicAssetIns)

      console.log('canvas', img)

      //commit("SET_UNIFORM_THUMBNAIL", canvas.toDataURL())*/
    },

    /**
     * This function returns an object containing availability for each content type
     *
     * Return Type
     * {
     *  Name: boolean,
     *  Number: boolean,
     *  Graphic: boolean
     * }
     * */

    //gets all products from each catalog, and searchs if at least one uses the location name
    //then we check if more than one sport has at least one product with that rule
    /* spiritwearLocationExistsInMoreThanOneSport({ rootState }, locationName) {
       const data = rootState.showroom.catalogs.map((catalog) => {
         const hasLocationName = catalog.products.find((product) => {
           return product.threekit_layout === locationName
         })
 
         return hasLocationName ? catalog.name : null
       })
 
       return data.filter((d) => {
         return d != null
       })
     },*/

    async getLocationContentTypes({ dispatch }, locationName) {
      const result = await dispatch("getLocations", locationName);
      if (!result) {
        throw new Error(
          `Location ${locationName} not found in PLP Decoration Locations data table`
        );
      }
      return result.contentTypes;
    },

    async getSnapshot(_, { api, options }) {
      //const player = window.api.enableApi("player");
      //player.cameraController.zoom(1);
      return await api.snapshotAsync({
        size: { width: options?.width ?? 470, height: options?.height ?? 588 },
      });
    },

    /** The configuration parameter object has the following optional properties
     * Name: string - team name
     * Number: string - team number
     * Graphic: {assetId} - id of the uploaded graphic
     * Font: {assetId} - id of the font file
     * Name Fill: {assetId} - id of the color of team name Fill
     * Name Outline: {assetId} - id of the color of team name Outline
     * Number Fill: {assetId} - id of the color of team number Fill
     * Number Outline: {assetId} - id of the color of team number Outline
     * Canvas X Axis Rotation: number -90 to 90
     * Canvas Y Axis Rotation: number -90 to 90
     *
     * When including a content type, if no outline is desired, one must pass {assetId: ''} to the respective outline attribute
     *
     * The options parameter property has the following optional properties
     * width: number - default is 470
     * height: number - default is 588
     *
     * It returns a base64 Blob that can be used directly in an html image element.
     **/
    async getLocationSnapshot(
      { state, dispatch },
      { locationName, configuration, options }
    ) {
      const result = await dispatch("getLocations", locationName);
      if (!result) {
        throw new Error(
          `Location ${locationName} not found in PLP Decoration Locations data table`
        );
      }
      await window.decorationsConfigurator?.setConfiguration({
        ...state.cachedTKData.datatables[DATATABLE_ID][locationName]
          .configuration,
        ...configuration,
      });
      return await dispatch("getSnapshot", {
        api: window.decorationsApi,
        options,
      });
    },

    async getForcedLocationSnapshot({ dispatch }, { configuration, options }) {
      await window.decorationsConfigurator?.setConfiguration({
        configuration,
      });
      return await dispatch("getSnapshot", {
        api: window.decorationsApi,
        options,
      });
    },

    /*getLocationFromStyle(_, style) {
      const rowQuery = {
        Style: {
          column: "Style",
          comparator: "=",
          value: style,
        },
      };
      return getDataTableRow(LAYOUT_TABLE_ID, rowQuery).then(res => res.value.Layout);
    },*/

    //await getSnapshotFromStyle('JR56BA',  window.configuration2);
    async getSnapshotFromStyle(
      { dispatch },
      { styleName, configuration, options }
    ) {
      const locationName = await dispatch("getLocationFromStyle", styleName);
      return await dispatch("getLocationSnapshot", {
        locationName,
        configuration,
        options,
      });
    },

    async get2DDecorationSnapshot({ dispatch }, { configuration, options }) {
      await window.decorationsConfigurator?.setConfiguration(configuration);
      return await dispatch("getSnapshot", {
        api: window.decorationsApi,
        options,
      });
    },

    async change3DView({ getters, state }, { view, locationName }) {
      //Case: We already have a view. Ex: we want to take snapshots for the locker preview
      const stageConfigurator = await window.stageApi.getConfigurator();

      if (view) {
        await stageConfigurator.setConfiguration({ View: view });
      }

      //We don't have a view. Ex: We are creating a new decoration or changing the location
      else if (locationName) {
        //Get the nested configurator of the decoration, so we can set the configuration
        const decorationNestedConfigurator = getters.getNestedConfigurator({
          parentConfigurator: state.decorationsConfigurator,
          attributeName: locationName,
        });

        const attributes = decorationNestedConfigurator.getDisplayAttributes();
        const viewAttribute = attributes.find((i) => i.name == "View");
        await stageConfigurator.setConfiguration({ View: viewAttribute.value });
      }
    },

    async get3DSnasphot({ dispatch }, view) {
      try {
        await dispatch("change3DView", { view });
        const snapshot = await dispatch("getSnapshot", {
          api: window.stageApi,
        });
        return snapshot;
      } catch (error) {
        console.log("get3DSnasphot error", error);
      }
    },

    async getImageProofSnapshots({ getters, dispatch }) {
      const snapshots = {};
      //Reset the view
      await dispatch("change3DView", { view: "Default" });
      await dispatch("changeReversibleView", "Outside");
      for (const view of CONSTANTS.VIEWS_3D) {
        const snapshot = await dispatch("get3DSnasphot", view);
        snapshots[view] = snapshot;
        console.log("snapshot", snapshot)
      }

      //Take Reversible snapshots
      if (getters["isReversible3D"]) {
        await dispatch("changeReversibleView", "Inside");
        await dispatch("change3DView", { view: "Default" });
        for (const view of CONSTANTS.VIEWS_3D) {
          const snapshot = await dispatch("get3DSnasphot", view);
          snapshots[`${view}_reversible`] = snapshot;
        }
      }
      return snapshots;
    },

    async saveActiveProductConfiguration({ rootState, state }) {
      const REST_API = `${TK_ENV}api/configurations?bearer_token=${AUTH_TOKEN}`;

      const fullConfig = state.globalConfigurator.getFullConfiguration();
      // const player = window.playerApi;

      const productId =
        state.cachedTKData.products[rootState.project.activeProduct.styleName]
          .item.id;
      const formData = new FormData();
      formData.append("orgId", ORG_ID);
      formData.append("productId", productId);
      // formData.append("productId", player.assetId);
      formData.append("productVersion", "1.0");
      formData.append("variant", JSON.stringify(fullConfig));

      const res = await fetch(REST_API, {
        method: "POST",
        body: formData,
      }).then((response) => response.json());
      console.log("saveActiveProductConfiguration res", res);
      return res;
    },

    async getSavedProductConfiguration(_, shortId) {
      //37OuVVvjz
      console.log("shortId: ", shortId);
      const REST_API = `${TK_ENV}api/configurations?bearer_token=${AUTH_TOKEN}&shortId=${shortId}`;

      const res = await fetch(REST_API, {
        method: "GET",
      }).then((response) => response.json());
      console.log("Saved configuration", res?.configurations);
      return res && res?.configurations?.[0];
    },

    setLoadingState({ commit }, { loadingState, message }) {
      commit("SET_LOADING_STATE", { loadingState, message });
    },

    setPlayerLoadingState({ commit }, { loadingState, message }) {
      commit("SET_PLAYER_LOADING_STATE", { loadingState, message });
    },

    setCompleteLoaderState({ commit }, { loadingState }) {
      commit("SET_COMPLETE_LOADER", { loadingState });
    },
  },

  mutations: {
    SET_GLOBAL_CONFIGURATOR(state, playerConfigurator) {
      Vue.set(state, "globalConfigurator", playerConfigurator);
    },

    SET_DECORATIONS_CONFIGURATOR(state, decorationsConfigurator) {
      console.log("SET_DECORATIONS_CONFIGURATOR", decorationsConfigurator);
      Vue.set(state, "decorationsConfigurator", decorationsConfigurator);
    },

    SET_ACTIVE_DECORATION_ATTRIBUTES(state, attrs) {
      Vue.set(state, "activeDecorationAttributes", attrs);
    },

    SET_DESIGN_CONFIGURATOR(state, designConfigurator) {
      console.log("SET_DESIGN_CONFIGURATOR", designConfigurator);
      Vue.set(state, "designConfigurator", designConfigurator);
    },

    SET_REVERSIBLE_VIEW(state, view) {
      const views = ["Outside", "Inside"];
      if (views.includes(view)) {
        Vue.set(state, "reversibleView", view);
      } else {
        throw new Error(`Invalid view ${view}`);
      }
    },

    //CACHE

    SET_PRODUCT_CACHE(state, { styleName, payload }) {
      //
      // console.log(' styleName, payload', styleName, payload);
      Vue.set(state.cachedTKData.products, styleName, payload);
    },

    //todo -> find a way to make deep changes based on a chain or something
    SET_PRODUCT_CACHE_STATE(state, { styleName, subState, payload }) {
      // console.log(' subState, payload', subState, payload);
      Vue.set(state.cachedTKData.products[styleName], subState, payload);
    },

    //todo -> find a way to make deep changes based on a chain or something
    SET_PRODUCT_CACHE_SUBSTATE(
      state,
      { styleName, parentState, subState, payload }
    ) {
      // console.log(' parentState,m subState, payload', parentState, subState, payload);
      Vue.set(state.cachedTKData.products[styleName][parentState], subState, {
        ...state.cachedTKData.products[styleName][parentState][subState],
        ...payload,
      });
    },

    SET_COLORS(state, { type, colors }) {
      Vue.set(state, type, colors);
    },

    //Decorations
    SET_DATA_TABLE(state, { dataTableId, dataTable }) {
      Vue.set(state.cachedTKData.datatables, dataTableId, dataTable);
    },

    SET_3D_LAYOUTS(state, layouts) {
      state.threekit3DLayouts = layouts;
    },

    SET_ACTIVE_DECORATION(
      state,
      {
        locationName,
        configuration,
        saveOriginalDecorationLocation,
        isNewDecoration,
      }
    ) {
      state.activeDecoration = {
        ...state?.activeDecoration,
        locationName,
        configuration,
        isNewDecoration: state?.activeDecoration?.isNewDecoration,
      };

      if (!state.activeDecoration) {
        throw new Error("activeDecoration is undefined or null");
      }

      if (saveOriginalDecorationLocation) {
        //This will help us to know if we are changing the decoration location
        state.activeDecoration.originalDecorationLocation = locationName;
      }

      if (isNewDecoration) {
        state.activeDecoration.isNewDecoration = isNewDecoration;
      }
    },

    SET_ACTIVE_DECORATION_THUMBNAIL(state, thumbnail) {
      state.activeDecoration.thumbnail = thumbnail;
    },

    CLEAR_ACTIVE_DECORATION(state) {
      state.activeDecoration = null;
    },

    SET_3D_DECORATIONS_LIST(state, decorations) {
      state.decorations3DList = decorations;
    },

    ADD_3D_DECORATION_TO_LIST(
      state,
      { locationName, configuration, reversibleView, thumbnail }
    ) {
      //todo filter by type 'spiritwear'/ 'uniforms' for Showrooms instead of the location
      const index = state.decorations3DList.findIndex(
        (item) =>
          item.locationName === locationName &&
          item.reversibleView === (reversibleView || state.reversibleView)
      );
      if (index > -1) {
        Vue.set(state.decorations3DList, index, {
          ...state.decorations3DList[index],
          locationName,
          configuration,
          thumbnail,
          reversibleView: reversibleView || state.reversibleView,
        });
      } else {
        state.decorations3DList.push({
          locationName,
          configuration,
          thumbnail,
          reversibleView: reversibleView || state.reversibleView,
        });
      }
    },

    CHANGE_3D_DECORATION_IN_LIST(
      state,
      { originalDecorationLocation, locationName, configuration, thumbnail }
    ) {
      const index = state.decorations3DList.findIndex(
        (item) =>
          item.locationName === originalDecorationLocation &&
          item.reversibleView === state.reversibleView
      );
      if (index > -1) {
        Vue.set(state.decorations3DList, index, {
          ...state.decorations3DList[index],
          locationName,
          configuration,
          thumbnail,
          reversibleView: state.reversibleView,
        });
      } else {
        state.decorations3DList.push({
          locationName,
          configuration,
          thumbnail,
          reversibleView: state.reversibleView,
        });
      }
    },

    REMOVE_3D_DECORATION_FROM_LIST(state, locationName) {
      state.decorations3DList = state.decorations3DList.filter((item) => {
        return item.locationName === locationName
          ? item.reversibleView !== state.reversibleView
          : true;
      });
    },

    CLEAR_3D_DECORATIONS_LIST(state) {
      state.decorations3DList = [];
    },

    SET_3D_DECORATION_THUMBNAIL(state, { locationName, thumbnail, view }) {
      const index = state.decorations3DList.findIndex(
        (item) =>
          item.locationName === locationName &&
          item.reversibleView === state.reversibleView
      );
      if (index > -1) {
        Vue.set(state.decorations3DList[index], "thumbnail", thumbnail);
        Vue.set(state.decorations3DList[index], "view", view);
      }
    },

    SET_SPIRITWEAR_THUMBNAIL(state, value) {
      state.spiritwearThumbnail = value;
    },

    SET_UNIFORM_THUMBNAIL(state, value) {
      state.uniformThumbnail = value;
    },

    ADD_2D_DECORATION_TO_LIST(state, { locationName, configuration }) {
      const index = state.PLPDecorationsList.findIndex(
        (item) => item.locationName !== locationName
      );
      if (index > -1) {
        Vue.set(state.PLPDecorationsList, index, {
          locationName,
          configuration,
        });
      }
    },

    SET_DECORATIONS_PRICE(state, price) {
      state.decorationsPrice = isNumber(price) && price ? price : 0;
    },

    SET_MATERIAL_COLORS(state, payload) {
      state.materialColors[payload.materialType] = payload.colors;
    },

    SET_FONTS(state, fonts) {
      state.fonts = fonts;
    },
    SET_THREEKIT_FONTS_IS_LOADING(state) {
      state.threeekitFontsIsLoading = true;
    },

    SET_LOADING_STATE(state, { loadingState, message }) {
      state.loading = loadingState;
      state.loadingMessage = message;
    },

    SET_COMPLETE_LOADER(state, { loadingState }) {
      state.completeLoader = loadingState;
    },

    SET_PLAYER_LOADING_STATE(state, { loadingState, message }) {
      state.playerLoader = loadingState;
      state.playerLoaderMessage = message;
    },

    SET_THREEKIT_LOADING_DATASET(state, dataset) {
      state.threekitLoadingDataset[dataset] = true;
    },
  },
};

export default threekit;
